/**
 * Unified Intelligence System
 * 
 * Single source of truth for ALL user analysis and metrics.
 * Replaces fragmented calculations across multiple components.
 * 
 * Philosophy:
 * - One analysis engine, many views
 * - Learn from data, predict the future
 * - Generate insights, not just numbers
 * - Actionable recommendations
 */

import type { SessionLog, PTSessionLog } from './sessionStorage';
import { computeSetVolume, calculateStrengthTonnageLbs } from './volumeTracking';
import type { RecoveryMetrics } from './types/recovery';
import type { UserAdaptiveProfile } from './types/userProfile';
import type { NutritionAnalysisResult } from './types/nutrition';
import { getUserProfile } from './userProfileService';
import { getRecoveryHistory } from './recoveryCalculator';
import { calculateRecoveryDebt } from './recoveryDebt';
import { detectPlateaus } from './plateauDetection';
// NOTE: mlPredictor is dynamically imported in initializeML() to avoid
// loading TensorFlow.js (and triggering its WebGL backend init) on every
// page that imports unifiedIntelligence. TF.js is heavy and its WebGL
// initialization can fail on browsers/devices without GPU support, which
// throws errors that break the Next.js error overlay across the app.
type MLPredictor = import('./mlPredictor').MLPredictor;
import { calculateBattleReadiness } from './operatorStandards';
import { calculateSessionTrainingLoad } from './trainingLoad';
import { loadProgressionDecisions, type ProgressionDecision } from './autoProgress';
import { loadIntervalProgressionDecisions, type IntervalProgressionDecision } from './adaptiveInterval';
import { getCardioProgressionSummary } from './adaptiveCardioPace';
import { mean as ssMean } from 'simple-statistics';
import { storage } from './storage';
import { isWeekComparable } from './dateUtils';
import { LAUNCH_FLAGS } from './launchFlags';

/**
 * Extended analysis data (ACWR, MED, SFR, biometrics, assessments)
 * Populated by gatherExtendedData() for downstream consumers
 */
export interface ExtendedIntelligenceData {
  muscleStatus: {
    byMuscleGroup: Record<string, { acwr: number; riskLevel: 'safe' | 'caution' | 'danger'; recommendation: string }>;
    overallACWR: number;
    overallRisk: 'safe' | 'caution' | 'danger';
    trainingMonotony: number;
  };
  volumeStatus: {
    overallVolumeStatus: string;
    totalWeeklySets: number;
    recommendedTotalSets: number;
    muscleIssues: Array<{ muscle: string; status: string; adjustmentNeeded: number }>;
  };
  exerciseEfficiency: {
    bestExercises: Array<{ name: string; sfr: number }>;
    worstExercises: Array<{ name: string; sfr: number }>;
  };
  energySummary: {
    workoutCalories: number;
    neatCalories: number;
    totalCalories: number;
    steps: number;
    stepGoal: number;
    neatScore: number;
    hasWearableData: boolean;
  };
  biometrics: {
    rhrCurrent: number | null;
    rhrBaseline: number | null;
    rhrStatus: string | null;
    hrvCurrent: number | null;
    hrvBaseline: number | null;
    hrvStatus: string | null;
    sleepMinutes: number | null;
  } | null;
  assessmentGrades: {
    hasAssessment: boolean;
    overallGrade: string | null;
    overallScore: number | null;
    categoryScores: { strength: number; endurance: number; workCapacity: number } | null;
    testGrades: Array<{ testName: string; grade: string; score: number; meetsMinimum: boolean }>;
    /** ISO date of the most-recent assessment, when one exists. */
    lastAssessedAt: string | null;
  };
  bodyComposition: {
    currentWeightKg: number | null;
    currentWeightLbs: number | null;
    currentBodyFatPercent: number | null;
    leanMassLbs: number | null;
    fatMassLbs: number | null;
    baselineWeightKg: number | null;
    weightDeltaKg: number | null;
    baselineBodyFatPercent: number | null;
    bodyFatDeltaPercent: number | null;
    dataPoints: number;
    hasData: boolean;
  } | null;
  nutritionStatus: {
    calorieAdherence: number;       // avg % (100 = perfect)
    proteinPerLbBodyweight: number | null;
    proteinAdequacy: 'low' | 'adequate' | 'high';
    calorieBalance: number;         // avg daily surplus/deficit kcal
    balanceStatus: 'deficit' | 'maintenance' | 'surplus';
    streak: number;
    daysTracked?: number;           // days with actual logging data in the window
    daysAnalyzed?: number;          // total days in the analysis window (usually 7)
    macroBalance: { protein: number; carbs: number; fat: number };
  } | null;
}

/**
 * Unified Intelligence Output
 * Everything you need to know about the user, right now
 */
export interface IntelligenceReport {
  // Current State
  currentState: {
    readiness: number;                    // 0-100, today's readiness
    /**
     * True when `readiness` is the baseline CONSTANT (70), not a measurement:
     * the user has logged no recovery today AND none ever. A fabricated number
     * must never drive a "train hard" order or render as a confident green score
     * — surfaces should show a calibrating/awaiting-data state instead.
     */
    readinessIsProvisional: boolean;
    fitness: number;                      // 0-100, accumulated fitness
    fatigue: number;                      // 0-100, accumulated fatigue
    recoveryDebt: number;                 // 0-100, recovery needed
    weeklyVolume: number;                 // This week's tonnage
    weeklySessionCount: number;           // This week's sessions
    currentStreak: number;                // Consecutive training days (1-day gaps OK)
    /**
     * Today's subjective recovery inputs as logged by the user (1-10 each).
     * `null` when no recovery entry exists for today — used by the Daily
     * Readiness card to show "your inputs" alongside the composite. These
     * are CAPTURED but were never SURFACED before — see stats POC §0.1.
     */
    todaySubjective: {
      soreness: number;
      stress: number;
      energy: number;
      motivation: number;
      sleepHours: number;
    } | null;
    programWeek: number;                  // Current program week
    programProgress: number;              // % through current program
    operatorScore: number;                // 0-100, composite battle readiness
    operatorScoreBreakdown: {             // Component scores
      strength: number;                   // 0-30
      endurance: number;                  // 0-25
      bodyweight: number;                 // 0-20
      recovery: number;                   // 0-15
      consistency: number;                // 0-10
    };
    // Data confidence: which pillars use real data vs defaults
    dataConfidence: {
      bodyweightIsDefault: boolean;       // True if bodyweight fell back to 180
      acwrDataSufficient: boolean;        // True if 4+ training days for ACWR
      hasStrengthData: boolean;           // True if any 1RM is set
      hasEnduranceData: boolean;          // True if ruck/run time or conditioning logged
      hasBodyweightData: boolean;         // True if pull-ups/push-ups max detected
    };
    // NEAT / daily activity (optional, null when no wearable)
    neatScore?: number;                   // 0-100 composite
    activityZone?: string;                // ActivityZone
    stepsToday?: number;
    activeMinutesToday?: number;
  };

  // Patterns (Learned)
  patterns: {
    recoveryCapacity: 'low' | 'average' | 'high';
    recoverySpeed: 'slow' | 'average' | 'fast';
    rpeAccuracy: 'conservative' | 'accurate' | 'aggressive';
    progressionRate: number;              // % per session
    optimalFrequency: number;             // sessions/week
    optimalRestDays: number;              // days between sessions
  };

  // Predictions
  predictions: {
    nextSessionPerformance: {
      expected: 'poor' | 'below-average' | 'average' | 'good' | 'excellent';
      confidence: number;                 // 0-1
      reasoning: string;
    };
    deloadRecommendation: {
      needed: boolean;
      urgency: 'low' | 'medium' | 'high' | 'critical';
      recommendedDate: Date | null;
      reasoning: string;
    };
    injuryRisk: {
      level: 'low' | 'moderate' | 'high' | 'critical';
      factors: string[];
      confidence: number;
    };
    plateauWarnings: Array<{
      exercise: string;
      weeksSinceProgress: number;
      suggestedIntervention: string;
    }>;
  };

  // Insights (Natural Language)
  insights: {
    primary: string;                      // Main takeaway
    /**
     * The verdict half of `primary`, without the leading telemetry clause
     * ("Readiness 79/100 · ACWR 0.74 (low)."). Surfaces that already render
     * those scalars — the SITREP hero telemetry strip does — must use this,
     * so one number is not printed twice from two different engines.
     * The ACWR here comes from muscleStatus (enhancedACWR); the hero's comes
     * from recoveryDebt. They legitimately differ, which is exactly why only
     * one surface may own it.
     */
    primaryVerdict: string;
    secondary: string[];                  // Supporting insights
    warnings: string[];                   // Things to watch
    wins: string[];                       // Recent achievements
  };

  // Recommendations (Actionable)
  recommendations: {
    today: {
      action: string;                     // What to do today
      reasoning: string;                  // Why
      intensity: 'light' | 'moderate' | 'hard' | 'max';
    };
    thisWeek: {
      focus: string;                      // Weekly goal
      adjustments: string[];              // Specific changes
    };
    nextWeek: {
      outlook: string;                    // What's coming
      preparation: string[];              // How to prepare
    };
  };

  // Confidence
  confidence: {
    overall: number;                      // 0-1
    dataQuality: number;                  // 0-1
    sessionsAnalyzed: number;
    daysInSystem: number;
    hasEnoughDataForML: boolean;          // true if >= 20 sessions
  };

  // Metadata
  generatedAt: Date;
  profileVersion: number;

  // Extended analysis (ACWR, MED, SFR, biometrics, assessments)
  extended?: ExtendedIntelligenceData;

  // Reentry classification — drives absence-aware mission filtering and
  // welcome-back UI. Always present after the engine populates it.
  reentry?: import('./reentryState').ReentryState;

  // ============================================================================
  // EXERCISE PROGRESSIONS (from adaptive systems)
  // ============================================================================
  progressions: {
    // Weight training progressions
    weights: {
      ready: Array<{
        exerciseName: string;
        currentWeight: number;
        suggestedWeight: number;
        reason: string;
        confidence: 'high' | 'medium' | 'low';
      }>;
      decreasing: Array<{
        exerciseName: string;
        currentWeight: number;
        suggestedWeight: number;
        reason: string;
      }>;
      totalTracked: number;
    };

    // Interval/HIIT progressions
    intervals: {
      ready: Array<{
        exerciseName: string;
        currentFormat: string;
        suggestedChange: string;
        reason: string;
        avgFatigueDrop: number;
      }>;
      needsReduction: Array<{
        exerciseName: string;
        reason: string;
      }>;
      totalTracked: number;
    };

    // Cardio/ruck progressions
    cardio: {
      recommendations: string[];
      vdot: number | undefined;
      hasData: boolean;
    };
  };

  // Short-term trends and deltas (user-facing)
  trends: {
    // Week-to-date (Mon -> today) vs previous week-to-date (same weekday)
    weekToDate: {
      strengthTonnageLbs: number;
      sessionsCompleted: number;
      ruckLoadMiles: number; // lb·mi (load × miles), includes plate carrier if present
      ruckMiles: number;
    };
    weekToDateDelta: {
      strengthTonnageLbs: number;
      sessionsCompleted: number;
      ruckLoadMiles: number;
      ruckMiles: number;
    };
    // 7-day readiness history (today inclusive)
    readiness7d: Array<{ date: string; readiness: number | null }>;
    // Operator Score delta vs 7 days ago (uses prior-day readiness when available)
    operatorScoreDelta7d: number;
    // Daily activity trends (optional, null when no wearable)
    activity?: {
      avgSteps7d: number;
      avgSteps30d: number;
      trend: string;
      suppressionDetected: boolean;
    };
  };
}

/**
 * Unified Intelligence Engine
 * The brain of the system
 */
// Pre-fetched data to avoid N+1 queries in calculateOperatorScore
export interface IntelligenceInitData {
  userId: string | null;
  oneRMs: Record<string, number>;
  assessments: any[];
  bodyweight: number;
  todayActivity?: { steps: number; activeMinutes: number; activityZone: string; neatScore: number; hrv?: number | null; restingHeartRate?: number | null; sleepMinutes?: number | null; activeCalories?: number; weight?: number | null; bodyFatPercent?: number | null } | null;
  activityTrend?: { avgSteps7d: number; avgSteps30d: number; trend: string; percentChange: number; suppressionDetected: boolean } | null;
  neatSuppression?: { detected: boolean; severity: string; dropPercent: number; message: string } | null;
  activityEntries?: Array<{ hrv?: number; restingHeartRate?: number; weight?: number; bodyFatPercent?: number }>;
  nutritionData?: NutritionAnalysisResult | null;
}

export class UnifiedIntelligence {
  private sessions: SessionLog[];
  private recoveryData: RecoveryMetrics[];
  private profile: UserAdaptiveProfile | null;
  private mlPredictor: MLPredictor | null = null;
  private useML = true; // Toggle for ML vs statistical fallback

  // Cached data to avoid repeated imports and queries
  private userId: string | null;
  private oneRMs: Record<string, number>;
  private assessments: any[];
  private bodyweight: number;
  private bodyweightIsDefault: boolean;

  // NEAT / daily activity data (optional, null when no wearable)
  private todayActivity: IntelligenceInitData['todayActivity'];
  private activityTrend: IntelligenceInitData['activityTrend'];
  private neatSuppression: IntelligenceInitData['neatSuppression'];
  private activityEntries: Array<{ hrv?: number; restingHeartRate?: number; weight?: number; bodyFatPercent?: number }>;
  private nutritionData: NutritionAnalysisResult | null;

  constructor(
    sessions: SessionLog[],
    recoveryData: RecoveryMetrics[],
    profile: UserAdaptiveProfile | null,
    initData?: IntelligenceInitData
  ) {
    this.sessions = sessions;
    this.recoveryData = recoveryData;
    this.profile = profile;

    // Use pre-fetched data to avoid N+1 queries
    this.userId = initData?.userId ?? null;
    this.oneRMs = initData?.oneRMs ?? {};
    this.assessments = initData?.assessments ?? [];
    this.bodyweight = initData?.bodyweight ?? 180;
    this.bodyweightIsDefault = !initData?.bodyweight || initData.bodyweight <= 0;

    // NEAT data (optional)
    this.todayActivity = initData?.todayActivity ?? null;
    this.activityTrend = initData?.activityTrend ?? null;
    this.neatSuppression = initData?.neatSuppression ?? null;
    this.activityEntries = initData?.activityEntries ?? [];
    this.nutritionData = initData?.nutritionData ?? null;

    // Initialize ML models if enough data — gated to skip ML for users
    // who don't have enough sessions to even use it. This avoids loading
    // TensorFlow.js (and its WebGL backend init) for new users entirely.
    if (this.sessions.length >= 20) {
      this.initializeML();
    } else {
      this.useML = false;
    }
  }

  private async initializeML() {
    // Neural-net predictor disabled — see LAUNCH_FLAGS.tensorflowModels.
    // Returning before the dynamic import means TensorFlow.js is never
    // fetched or evaluated. mlPredictor stays null, and every consumer
    // below already guards on `this.mlPredictor` before using it.
    if (!LAUNCH_FLAGS.tensorflowModels) return;
    try {
      // Lazy-import the ML predictor only when actually needed.
      // This keeps TensorFlow.js out of the main bundle and avoids
      // WebGL backend initialization on browsers that don't support it.
      const { getMLPredictor } = await import('./mlPredictor');
      this.mlPredictor = getMLPredictor();

      // Try to load existing models
      const loaded = await this.mlPredictor.loadModels();

      if (!loaded && this.sessions.length >= 20) {
        // Train new models if enough data
        console.log('Training ML models on user data...');
        const result = await this.mlPredictor.trainModels(this.sessions, this.recoveryData);

        if (result.success) {
          await this.mlPredictor.saveModels();
          console.log('ML models ready:', result.message);
        } else {
          console.log('Using statistical methods:', result.message);
          this.useML = false;
        }
      } else if (!loaded) {
        console.log('Not enough data for ML. Using statistical methods.');
        this.useML = false;
      }
    } catch (error) {
      console.error('ML initialization failed (using statistical methods):', error);
      this.useML = false;
    }
  }

  /**
   * Generate complete intelligence report
   */
  async analyze(): Promise<IntelligenceReport> {
    const currentState = await this.analyzeCurrentState();
    const patterns = this.analyzePatterns();
    const predictions = await this.generatePredictions();
    const confidence = this.assessConfidence();
    const trends = await this.calculateTrends(currentState);
    const progressions = await this.gatherProgressions();
    const extended = await this.gatherExtendedData();

    // Reentry classification — must be computed before recommendations so
    // "Today's Orders" can branch on returning/drifting status. Without it,
    // the recommender sees readiness=70 (because no fatigue accumulated)
    // and prescribes "hard" sessions to a user who's been gone two weeks.
    let reentry: IntelligenceReport['reentry'];
    try {
      const { reentryStateFromHistory } = await import('./reentryState');
      reentry = reentryStateFromHistory(
        this.sessions ?? [],
        this.recoveryData ?? [],
        [],
      );
      // Stash on currentState so generateRecommendations can read it without
      // needing a wider signature change.
      (currentState as any).reentryStatus = reentry?.status;
      (currentState as any).reentryDays = reentry?.daysSinceLastSession;
    } catch (err) {
      // Non-critical — engine still returns a report without reentry info.
      console.warn('[intelligence] reentry compute failed:', err);
    }

    // Insights and recommendations generated last so they can incorporate all data
    const insights = this.generateInsights(currentState, patterns, predictions, progressions, extended, trends);
    const recommendations = this.generateRecommendations(currentState, patterns, predictions, progressions, extended);

    return {
      currentState,
      patterns,
      predictions,
      insights,
      recommendations,
      confidence,
      progressions,
      trends,
      extended,
      reentry,
      generatedAt: new Date(),
      profileVersion: this.profile?.version || 1,
    };
  }

  /**
   * Gather all exercise progressions from adaptive systems
   */
  private async gatherProgressions(): Promise<IntelligenceReport['progressions']> {
    // Weight training progressions — try stored decisions first, regenerate from history if empty
    let weightProgressions: ProgressionDecision[] = [];
    try {
      weightProgressions = await loadProgressionDecisions();
      console.log(` Loaded ${weightProgressions.length} weight progression decisions`);

      // If no stored decisions exist but we have sessions, regenerate from the most recent session
      if (weightProgressions.length === 0 && this.sessions.length > 0) {
        try {
          const { analyzeSessionForProgression } = await import('./autoProgress');
          // Find the most recent PT session with exercises
          const recentPT = [...this.sessions]
            .filter(s => s.sessionType === 'pt' && 'exercises' in s && (s as any).exercises?.length > 0)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 1);

          if (recentPT.length > 0) {
            const result = await analyzeSessionForProgression(recentPT[0] as any);
            if (result && result.progressions && result.progressions.length > 0) {
              weightProgressions = result.progressions;
              console.log(` Regenerated ${result.progressions.length} progression decisions from latest session`);
            }
          }
        } catch (err) {
          console.warn(' Failed to regenerate progressions from history:', err);
        }
      }
    } catch (error) {
      console.warn(' Failed to load weight progressions:', error);
    }

    const weightsReady = weightProgressions
      .filter(p => p.action === 'increase')
      .map(p => ({
        exerciseName: p.exerciseName,
        currentWeight: p.currentWeight,
        suggestedWeight: p.suggestedWeight,
        reason: p.reason,
        confidence: p.confidence,
      }));

    const weightsDecreasing = weightProgressions
      .filter(p => p.action === 'decrease')
      .map(p => ({
        exerciseName: p.exerciseName,
        currentWeight: p.currentWeight,
        suggestedWeight: p.suggestedWeight,
        reason: p.reason,
      }));

    // Interval/HIIT progressions
    let intervalProgressions: IntervalProgressionDecision[] = [];
    try {
      intervalProgressions = await loadIntervalProgressionDecisions();
      console.log(`‍ Loaded ${intervalProgressions.length} interval progression decisions`);
    } catch (error) {
      console.warn(' Failed to load interval progressions:', error);
    }

    const intervalsReady = intervalProgressions
      .filter(p => p.readyForProgression)
      .map(p => ({
        exerciseName: p.exerciseName,
        currentFormat: p.currentFormat,
        suggestedChange: p.progressionPath === 'add_intervals'
          ? `Add interval ${p.suggestedIntervals}`
          : p.progressionPath === 'upgrade_format'
          ? `Upgrade to ${p.suggestedFormat}`
          : p.progressionPath === 'reduce_rest'
          ? 'Reduce rest periods'
          : p.progressionPath,
        reason: p.reason,
        avgFatigueDrop: p.avgFatigueDrop,
      }));

    const intervalsNeedsReduction = intervalProgressions
      .filter(p => p.progressionPath === 'deload')
      .map(p => ({
        exerciseName: p.exerciseName,
        reason: p.reason,
      }));

    // Cardio/ruck progressions
    let cardioSummary: { hasData: boolean; recommendations: string[]; vdot: number | undefined } = {
      hasData: false,
      recommendations: [],
      vdot: undefined
    };
    try {
      const cardioResult = await getCardioProgressionSummary();
      cardioSummary = {
        hasData: cardioResult.hasData,
        recommendations: cardioResult.recommendations,
        vdot: cardioResult.vdot ?? undefined,
      };
      console.log(` Loaded cardio progression summary: ${cardioSummary.recommendations.length} recommendations`);
    } catch (error) {
      console.warn(' Failed to load cardio progressions:', error);
    }

    return {
      weights: {
        ready: weightsReady,
        decreasing: weightsDecreasing,
        totalTracked: weightProgressions.length,
      },
      intervals: {
        ready: intervalsReady,
        needsReduction: intervalsNeedsReduction,
        totalTracked: intervalProgressions.length,
      },
      cardio: {
        recommendations: cardioSummary.recommendations,
        vdot: cardioSummary.vdot,
        hasData: cardioSummary.hasData,
      },
    };
  }

  /**
   * Gather extended analysis data (ACWR, MED, SFR, biometrics, assessments)
   * Each sub-call is wrapped in .catch so one failure doesn't kill the whole brief
   */
  private async gatherExtendedData(): Promise<ExtendedIntelligenceData | undefined> {
    try {
      // 1. Run ACWR, MED, SFR in parallel — pass cached sessions to avoid redundant queries
      const s = this.sessions;
      const [acwrResult, medResult, sfrResult] = await Promise.all([
        import('./enhancedACWR').then(m => m.calculateEnhancedACWR(s)).catch(() => null),
        import('./minimumEffectiveDose').then(m => m.analyzeMED(s)).catch(() => null),
        import('./stimulusFatigueRatio').then(m => m.calculateSFR(s)).catch(() => null),
      ]);

      // 2. Build muscle status from ACWR
      const byMuscleGroup: Record<string, { acwr: number; riskLevel: 'safe' | 'caution' | 'danger'; recommendation: string }> = {};
      if (acwrResult?.byMuscleGroup) {
        for (const [muscle, data] of Object.entries(acwrResult.byMuscleGroup)) {
          byMuscleGroup[muscle] = {
            acwr: data.acwr,
            riskLevel: data.riskLevel,
            recommendation: data.recommendation,
          };
        }
      }
      const muscleStatus = {
        byMuscleGroup,
        overallACWR: acwrResult?.overallACWR ?? 1.0,
        overallRisk: (acwrResult?.overallRisk ?? 'safe') as 'safe' | 'caution' | 'danger',
        trainingMonotony: acwrResult?.trainingMonotony ?? 1.0,
      };

      // 3. Build volume status from MED
      const muscleIssues: Array<{ muscle: string; status: string; adjustmentNeeded: number }> = [];
      if (medResult?.byMuscleGroup) {
        for (const [muscle, data] of Object.entries(medResult.byMuscleGroup)) {
          if (data.status !== 'optimal') {
            muscleIssues.push({ muscle, status: data.status, adjustmentNeeded: data.adjustmentNeeded });
          }
        }
      }
      const volumeStatus = {
        overallVolumeStatus: medResult?.overallVolumeStatus ?? 'unknown',
        totalWeeklySets: medResult?.totalWeeklySets ?? 0,
        recommendedTotalSets: medResult?.recommendedTotalSets ?? 0,
        muscleIssues,
      };

      // 4. Build exercise efficiency from SFR
      const exerciseEfficiency = {
        bestExercises: (sfrResult?.bestExercises ?? []).slice(0, 5).map(e => ({ name: e.exerciseName, sfr: e.sfr })),
        worstExercises: (sfrResult?.worstExercises ?? []).slice(0, 3).map(e => ({ name: e.exerciseName, sfr: e.sfr })),
      };

      // 5. Build energy summary from today's sessions
      const todayStr = new Date().toDateString();
      const todaySessions = this.sessions.filter(s => new Date(s.date).toDateString() === todayStr);
      let workoutCalories = 0;
      try {
        const { estimateSessionCalories } = await import('./calorieEstimator');
        for (const session of todaySessions) {
          const summary = estimateSessionCalories(session, this.bodyweight);
          workoutCalories += summary.totalCalories;
        }
      } catch { /* non-critical */ }

      const neatCalories = this.todayActivity?.activeCalories ?? 0;

      // Pull the user's actual stepGoal from their profile (set during
      // onboarding's daily-targets step). Fall back to 8000 if no profile
      // — that's the standard public-health baseline.
      let stepGoal = 8000;
      if (this.userId) {
        try {
          const { db: dexieDb } = await import('./db');
          const profile = await dexieDb.userProfiles.get(this.userId);
          if (profile?.stepGoal && profile.stepGoal > 0) {
            stepGoal = profile.stepGoal;
          }
        } catch { /* non-critical */ }
      }

      const energySummary = {
        workoutCalories,
        neatCalories,
        totalCalories: workoutCalories + neatCalories,
        steps: this.todayActivity?.steps ?? 0,
        stepGoal,
        neatScore: this.todayActivity?.neatScore ?? 0,
        hasWearableData: !!this.todayActivity,
      };

      // 6. Build biometric snapshot
      let biometrics: ExtendedIntelligenceData['biometrics'] = null;
      if (this.todayActivity && (this.todayActivity.hrv || this.todayActivity.restingHeartRate)) {
        try {
          const { calculateHRVBaseline, calculateRHRBaseline, checkHRVStatus, checkRHRStatus } = await import('./biometricBaselines');
          const entries = this.activityEntries;
          const hrvBaseline = calculateHRVBaseline(entries as any[]);
          const rhrBaseline = calculateRHRBaseline(entries as any[]);
          const hrvCurrent = this.todayActivity.hrv ?? null;
          const rhrCurrent = this.todayActivity.restingHeartRate ?? null;
          const hrvStatus = hrvCurrent ? checkHRVStatus(hrvCurrent, hrvBaseline) : null;
          const rhrStatus = rhrCurrent ? checkRHRStatus(rhrCurrent, rhrBaseline) : null;
          biometrics = {
            rhrCurrent,
            rhrBaseline: rhrBaseline.hasEnoughData ? rhrBaseline.avg30d : null,
            rhrStatus: rhrStatus?.status ?? null,
            hrvCurrent,
            hrvBaseline: hrvBaseline.hasEnoughData ? hrvBaseline.avg30d : null,
            hrvStatus: hrvStatus?.status ?? null,
            sleepMinutes: this.todayActivity.sleepMinutes ?? null,
          };
        } catch { /* non-critical */ }
      }

      // 7. Build assessment grades
      let assessmentGrades: ExtendedIntelligenceData['assessmentGrades'] = {
        hasAssessment: false,
        overallGrade: null,
        overallScore: null,
        categoryScores: null,
        testGrades: [],
        lastAssessedAt: null,
      };

      console.log(`[MissionBrief] Assessment count: ${this.assessments.length}`);
      if (this.assessments.length > 0) {
        try {
          const { calculateCategoryScores, calculateOverallGrade, gradeTest } = await import('./assessmentGrading');
          const { convertFitnessTestToUnified } = await import('./unifiedAssessment');
          const latest = [...this.assessments].sort((a: any, b: any) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
          )[0];

          console.log(`[MissionBrief] Latest assessment:`, {
            id: latest.id,
            date: latest.date,
            hasDay1: !!latest.day1,
            hasDay3: !!latest.day3,
            hasDay5: !!latest.day5,
            deadlift3RM: latest.deadlift3RM,
            benchPressBWMaxReps: latest.benchPressBWMaxReps,
            bodyweight: latest.bodyweight,
            keys: Object.keys(latest).filter(k => k !== 'id' && k !== 'userId'),
          });

          // Convert to unified format (handles both flat FitnessTest AND structured day1/day3/day5 formats)
          const unified = convertFitnessTestToUnified(latest);

          console.log(`[MissionBrief] Unified assessment:`, {
            deadlift: unified.deadlift,
            benchPress: unified.benchPress,
            plank: unified.plank,
            pushups: unified.pushups,
            run: unified.run,
            ruck: unified.ruck,
            tacticalAmrap: unified.tacticalAmrap,
            bodyweight: unified.bodyweight,
          });

          // Get user's program level
          let level: any = 'operator';
          try {
            if (this.userId) {
              const { getUserProgramSelection } = await import('./programManagement');
              const selection = await getUserProgramSelection(this.userId);
              if (selection?.programLevel) level = selection.programLevel;
            }
          } catch { /* use operator as default */ }

          // Build Day1, Day3, Day5 from unified assessment data
          const bw = unified.bodyweight || latest.bodyweight;
          const day1: any = {};
          if (unified.deadlift) day1.deadlift3RM = { weight: unified.deadlift };
          if (unified.benchPress) day1.benchPressMaxReps = { reps: unified.benchPress, bodyweight: bw };
          if (unified.plank) day1.plankMaxTime = { seconds: unified.plank };
          if (unified.pushups) day1.handReleasePushups = { reps: unified.pushups };
          if (unified.run) day1.run1_5Mile = { seconds: unified.run };

          const day3: any = {};
          if (unified.tacticalAmrap) {
            day3.tacticalAMRAP = { rounds: unified.tacticalAmrap };
          }

          const day5: any = {};
          if (unified.ruck) {
            day5.ruck6Mile = { seconds: unified.ruck };
          }

          console.log(`[MissionBrief] Day objects:`, {
            day1Keys: Object.keys(day1),
            day3Keys: Object.keys(day3),
            day5Keys: Object.keys(day5),
            level,
            bodyweight: bw,
          });

          const categoryScores = calculateCategoryScores(
            Object.keys(day1).length > 0 ? day1 : null,
            Object.keys(day3).length > 0 ? day3 : null,
            Object.keys(day5).length > 0 ? day5 : null,
            level,
            bw
          );
          const overall = calculateOverallGrade(categoryScores, level);

          console.log(`[MissionBrief] Grading results:`, {
            categoryScores,
            overallGrade: overall.grade,
            overallScore: overall.overallScore,
          });

          // Build individual test grades from unified values
          const testGrades: ExtendedIntelligenceData['assessmentGrades']['testGrades'] = [];
          if (unified.deadlift) {
            const g = gradeTest('deadlift', unified.deadlift, level, bw);
            testGrades.push({ testName: 'Deadlift', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }
          if (unified.benchPress) {
            const g = gradeTest('benchPress', unified.benchPress, level);
            testGrades.push({ testName: 'Bench Press', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }
          if (unified.run) {
            const g = gradeTest('run', unified.run, level);
            testGrades.push({ testName: '1.5mi Run', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }
          if (unified.plank) {
            const g = gradeTest('plank', unified.plank, level);
            testGrades.push({ testName: 'Plank', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }
          if (unified.pushups) {
            const g = gradeTest('pushups', unified.pushups, level);
            testGrades.push({ testName: 'Push-ups', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }
          if (unified.ruck) {
            const g = gradeTest('ruck', unified.ruck, level);
            testGrades.push({ testName: '6mi Ruck', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }
          if (unified.tacticalAmrap) {
            const g = gradeTest('tacticalAmrap', unified.tacticalAmrap, level);
            testGrades.push({ testName: 'AMRAP', grade: g.grade, score: g.score, meetsMinimum: g.meetsMinimum });
          }

          assessmentGrades = {
            hasAssessment: true,
            overallGrade: overall.grade,
            overallScore: overall.overallScore,
            categoryScores,
            testGrades,
            lastAssessedAt: latest.date ? new Date(latest.date).toISOString() : null,
          };
        } catch (error) {
          console.warn('Failed to compute assessment grades:', error);
        }
      }

      // 7.5. Build body composition from weight/body fat data
      let bodyComposition: ExtendedIntelligenceData['bodyComposition'] = null;
      const currentWeightKg = this.todayActivity?.weight ?? null;
      const currentBfPercent = this.todayActivity?.bodyFatPercent ?? null;
      if (currentWeightKg !== null) {
        const KG_TO_LBS = 2.20462;
        const currentWeightLbs = currentWeightKg * KG_TO_LBS;

        // Calculate lean/fat mass if body fat available
        let fatMassLbs: number | null = null;
        let leanMassLbs: number | null = null;
        if (currentBfPercent !== null) {
          fatMassLbs = Math.round((currentWeightLbs * (currentBfPercent / 100)) * 10) / 10;
          leanMassLbs = Math.round((currentWeightLbs - fatMassLbs) * 10) / 10;
        }

        // 30-day baselines from activity entries (same pattern as HRV/RHR)
        const weightEntries = this.activityEntries.filter(e => e.weight != null).map(e => e.weight!);
        const bfEntries = this.activityEntries.filter(e => e.bodyFatPercent != null).map(e => e.bodyFatPercent!);

        const baselineWeightKg = weightEntries.length >= 3 ? ssMean(weightEntries) : null;
        const baselineBodyFatPercent = bfEntries.length >= 3 ? ssMean(bfEntries) : null;

        bodyComposition = {
          currentWeightKg,
          currentWeightLbs: Math.round(currentWeightLbs * 10) / 10,
          currentBodyFatPercent: currentBfPercent,
          leanMassLbs,
          fatMassLbs,
          baselineWeightKg: baselineWeightKg !== null ? Math.round(baselineWeightKg * 100) / 100 : null,
          weightDeltaKg: baselineWeightKg !== null ? Math.round((currentWeightKg - baselineWeightKg) * 100) / 100 : null,
          baselineBodyFatPercent: baselineBodyFatPercent !== null ? Math.round(baselineBodyFatPercent * 10) / 10 : null,
          bodyFatDeltaPercent: baselineBodyFatPercent !== null && currentBfPercent !== null ? Math.round((currentBfPercent - baselineBodyFatPercent) * 10) / 10 : null,
          dataPoints: weightEntries.length,
          hasData: true,
        };
      }

      // 8. Build nutrition status from analysis data
      let nutritionStatus: ExtendedIntelligenceData['nutritionStatus'] = null;
      if (this.nutritionData && this.nutritionData.daysTracked > 0) {
        const nd = this.nutritionData;
        const ppLb = nd.proteinPerLbBodyweight;

        let proteinAdequacy: 'low' | 'adequate' | 'high' = 'adequate';
        if (ppLb !== null) {
          if (ppLb < 0.7) proteinAdequacy = 'low';
          else if (ppLb > 1.2) proteinAdequacy = 'high';
        }

        let balanceStatus: 'deficit' | 'maintenance' | 'surplus' = 'maintenance';
        if (nd.avgCalorieBalance < -200) balanceStatus = 'deficit';
        else if (nd.avgCalorieBalance > 200) balanceStatus = 'surplus';

        nutritionStatus = {
          calorieAdherence: nd.avgCalorieAdherence,
          proteinPerLbBodyweight: ppLb,
          proteinAdequacy,
          calorieBalance: nd.avgCalorieBalance,
          balanceStatus,
          streak: nd.streak,
          daysTracked: nd.daysTracked,
          daysAnalyzed: nd.daysAnalyzed,
          macroBalance: {
            protein: nd.avgProteinGrams,
            carbs: nd.avgCarbGrams,
            fat: nd.avgFatGrams,
          },
        };
      }

      return {
        muscleStatus,
        volumeStatus,
        exerciseEfficiency,
        energySummary,
        biometrics,
        assessmentGrades,
        bodyComposition,
        nutritionStatus,
      };
    } catch (error) {
      console.error('Failed to gather extended data:', error);
      return undefined;
    }
  }

  private async calculateTrends(currentState: any): Promise<IntelligenceReport['trends']> {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun..6=Sat
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);

    // The week-to-date window must END at the end of today, not the start of
    // it. Sessions carry a full timestamp, so comparing against startOfToday
    // excluded EVERY session logged today: a user finished a workout, opened
    // Stats, and "This Week vs Last" (and WeeklyPulse) didn't count it — while
    // the 5-Week Volume panel on the same screen did, because it buckets by
    // calendar week. That hit every user, every day they trained.
    // The previous-week window below already ends at 23:59:59.999.
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);

    const weekStart = new Date(startOfToday);
    weekStart.setDate(startOfToday.getDate() - daysFromMonday);

    const prevWeekStart = new Date(weekStart);
    prevWeekStart.setDate(weekStart.getDate() - 7);

    // Compare to last week at the same weekday (week-to-date delta)
    const prevWeekEnd = new Date(prevWeekStart);
    prevWeekEnd.setDate(prevWeekStart.getDate() + daysFromMonday);
    prevWeekEnd.setHours(23, 59, 59, 999);

    const thisWeekSessions = this.sessions.filter(s => {
      const d = new Date(s.date);
      const hasExercises = s.sessionType === 'range' ? true : (s as any).exercises && (s as any).exercises.length > 0;
      return d >= weekStart && d <= endOfToday && hasExercises;
    });

    const prevWeekSessions = this.sessions.filter(s => {
      const d = new Date(s.date);
      const hasExercises = s.sessionType === 'range' ? true : (s as any).exercises && (s as any).exercises.length > 0;
      return d >= prevWeekStart && d <= prevWeekEnd && hasExercises;
    });

    const strengthTonnageThis = this.calculateVolume(thisWeekSessions);
    const strengthTonnagePrev = this.calculateVolume(prevWeekSessions);

    const sessionsThis = thisWeekSessions.length;
    const sessionsPrev = prevWeekSessions.length;

    const ruckThis = this.calculateRuckLoadMiles(thisWeekSessions);
    const ruckPrev = this.calculateRuckLoadMiles(prevWeekSessions);

    const readiness7d = this.getReadinessHistoryDays(7);

    // Operator score delta vs 7 days ago (approx: same formula but using that day's readiness when available)
    const score7dAgo = await this.calculateOperatorScoreSnapshotDaysAgo(7) ?? currentState.operatorScore;
    const operatorScoreDelta7d = currentState.operatorScore - score7dAgo;

    return {
      weekToDate: {
        strengthTonnageLbs: strengthTonnageThis,
        sessionsCompleted: sessionsThis,
        ruckLoadMiles: ruckThis.loadMiles,
        ruckMiles: ruckThis.miles,
      },
      weekToDateDelta: {
        strengthTonnageLbs: strengthTonnageThis - strengthTonnagePrev,
        sessionsCompleted: sessionsThis - sessionsPrev,
        ruckLoadMiles: ruckThis.loadMiles - ruckPrev.loadMiles,
        ruckMiles: ruckThis.miles - ruckPrev.miles,
      },
      readiness7d,
      operatorScoreDelta7d,
    };
  }

  /** Most recent logged readiness score (any day), or null if none */
  private getLastKnownReadiness(): number | null {
    const sorted = [...this.recoveryData]
      .filter(r => r.readinessScore != null && r.readinessScore > 0)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return sorted.length > 0 ? sorted[0].readinessScore : null;
  }

  private getReadinessHistoryDays(days: number): Array<{ date: string; readiness: number | null }> {
    const out: Array<{ date: string; readiness: number | null }> = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toDateString();
      const rec = this.recoveryData.find(r => new Date(r.date).toDateString() === key);
      out.push({ date: d.toISOString().slice(0, 10), readiness: rec?.readinessScore ?? null });
    }
    return out;
  }

  private async calculateOperatorScoreSnapshotDaysAgo(daysAgo: number): Promise<number | null> {
    const target = new Date();
    target.setHours(0, 0, 0, 0);
    target.setDate(target.getDate() - daysAgo);
    const targetKey = target.toDateString();

    // Temporarily use readiness from that day if present, otherwise baseline
    const rec = this.recoveryData.find(r => new Date(r.date).toDateString() === targetKey);
    const baseline = this.profile?.recoveryProfile.baselineReadiness || 70;
    const readinessScore = rec?.readinessScore ?? baseline;

    // Recompute operator score using that day's readiness but same 1RMs/bodyweight, etc.
    // We keep it simple: only the recovery component changes day-to-day.
    const current = await this.calculateOperatorScore();
    const currentRecoveryRounded = current.breakdown.recovery;
    const targetRecovery = Math.round((readinessScore / 100) * 15);

    const adjustedTotal = current.totalScore - currentRecoveryRounded + targetRecovery;
    return adjustedTotal;
  }

  private calculateRuckLoadMiles(sessions: SessionLog[]): { loadMiles: number; miles: number } {
    let loadMiles = 0;
    let miles = 0;

    for (const session of sessions) {
      if (session.sessionType === 'range') continue;
      const exs = (session as any).exercises as any[] | undefined;
      if (!exs || exs.length === 0) continue;

      for (const ex of exs) {
        const t = (ex.exerciseType || '').toString().toLowerCase();
        const name = (ex.exerciseName || '').toString().toLowerCase();
        const isRuckLike =
          t === 'ruck' ||
          t === 'carry' ||
          name.includes('ruck') ||
          name.includes('carry') ||
          name.includes('farmer') ||
          name.includes('suitcase');

        const distMeters = typeof ex.actualDistance === 'number' ? ex.actualDistance : null;
        if (!isRuckLike || !distMeters || distMeters <= 0) continue;

        const load = (typeof ex.actualLoad === 'number' ? ex.actualLoad : 0) + (typeof ex.plateCarrierWeight === 'number' ? ex.plateCarrierWeight : 0);
        if (load <= 0) continue;

        const distMiles = distMeters / 1609.34;
        miles += distMiles;
        loadMiles += load * distMiles;
      }
    }

    return { loadMiles, miles };
  }

  /**
   * Analyze current state (what's happening NOW)
   */
  private async analyzeCurrentState() {
    const today = new Date();
    const todayStr = today.toDateString();
    
    // Today's readiness
    const todayRecovery = this.recoveryData.find(r =>
      new Date(r.date).toDateString() === todayStr
    );

    // Recovery debt (calculate first — we need needsDeload for readiness logic)
    const recoveryDebtObj = await calculateRecoveryDebt();
    const recoveryDebt = recoveryDebtObj.overallDebt;

    // Readiness: if recovery logged today, use it directly.
    // If NOT logged AND the app recommended rest (needsDeload / blocked training),
    // carry forward last known readiness + small rest bonus instead of falling to baseline.
    // This prevents penalizing users for following the app's own rest recommendation.
    const baseline = this.profile?.recoveryProfile.baselineReadiness || 70;

    /**
     * WEARABLE-DERIVED READINESS.
     *
     * A user who wears a device has a real overnight signal (HRV, RHR, sleep)
     * every morning whether or not they open the app and fill in a check-in.
     * Falling back to a constant for those users threw away data we already
     * had. `PersonalState` composes exactly that signal into a 0-100 score
     * with a confidence, so it is the right second source.
     *
     * `overallConfidence` is what makes it usable: `composeOverallReadiness`
     * returns `{score: 70, confidence: 0}` when no stream has data
     * (personalState.ts:472), i.e. the SAME placeholder 70. So the score is
     * only trustworthy when confidence is non-trivial — hence the floor
     * below rather than a bare null-check.
     */
    const WEARABLE_CONFIDENCE_FLOOR = 0.3;
    let wearableReadiness: number | null = null;
    try {
      if (this.userId) {
        const { loadPersonalState } = await import('./personalState');
        // Age is only used for age-adjusted HRV/RHR norms inside PersonalState;
        // null is an accepted input and simply skips that adjustment.
        const ps = await loadPersonalState(this.userId, null);
        if (ps && ps.overallConfidence >= WEARABLE_CONFIDENCE_FLOOR) {
          wearableReadiness = ps.overallReadiness;
        }
      }
    } catch {
      // Non-critical: no wearable signal, fall through to the logic below.
    }

    let readiness: number;
    if (todayRecovery?.readinessScore) {
      readiness = todayRecovery.readinessScore;
    } else if (wearableReadiness != null) {
      // No manual check-in, but the device reported. That IS a measurement.
      readiness = wearableReadiness;
    } else if (recoveryDebtObj.needsDeload) {
      // App told user to rest — don't punish with baseline fallback
      const lastKnown = this.getLastKnownReadiness();
      readiness = lastKnown != null ? Math.min(100, lastKnown + 3) : baseline;
    } else {
      readiness = baseline;
    }

    /**
     * Provisional = the number is a CONSTANT, not a measurement.
     *
     * True only when all three sources are empty: nothing logged today, no
     * wearable signal, and nothing ever on record. In that state the UI must
     * render NOTHING rather than a green "70" — a fabricated score that reads
     * as "cleared to train" is worse than an honest blank, and it is the
     * user's first impression before they have logged anything.
     *
     * A user WITH sessions but no recovery log still has a fitness-fatigue
     * basis, so this stays scoped to the pure-constant case.
     */
    const readinessIsProvisional =
      !todayRecovery?.readinessScore &&
      wearableReadiness == null &&
      this.getLastKnownReadiness() == null;

    // Fitness-Fatigue (from profile or calculate)
    const fitness = this.profile?.fitnessModel.currentFitness || this.calculateFitness();
    const fatigue = this.profile?.fitnessModel.currentFatigue || this.calculateFatigue();

    // Weekly metrics (Monday-Sunday calendar week)
    const weekStart = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days; otherwise go back to Monday
    weekStart.setDate(today.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);

    // Calendar week: Mon-Sun

    const thisWeekSessions = this.sessions.filter(s => {
      const sessionDate = new Date(s.date);
      const isInWeek = sessionDate >= weekStart && sessionDate <= today;
      
      // Only count sessions with actual exercises (exclude skipped sessions)
      const hasExercises = s.sessionType === 'range' 
        ? true // Range sessions don't have exercises array
        : (s as any).exercises && (s as any).exercises.length > 0;
      
      if (isInWeek) {
        console.log(`  ${hasExercises ? '' : '⏭'} Session on ${sessionDate.toDateString()} - ${hasExercises ? 'COMPLETED' : 'SKIPPED (no exercises)'}`);
      }
      
      return isInWeek && hasExercises;
    });

    const weeklySessionCount = thisWeekSessions.length;
    const weeklyVolume = this.calculateVolume(thisWeekSessions);

    console.log(` Calendar Week (Mon-Sun): ${weekStart.toLocaleDateString()} - ${today.toLocaleDateString()}`);
    console.log(` Sessions this week: ${weeklySessionCount}`);

    // Program progress
    const currentProgram = storage.get('currentProgram');
    const programStartDate = storage.get('programStartDate');
    let programWeek = 1;
    let programProgress = 0;

    if (programStartDate) {
      const startDate = new Date(programStartDate);
      const weeksSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      programWeek = weeksSinceStart + 1;

      // Estimate program length (most programs are 6-12 weeks)
      const estimatedLength = 8; // Default
      programProgress = Math.min(100, (programWeek / estimatedLength) * 100);
    }

    // Calculate Operator Score
    const { totalScore: operatorScore, breakdown: operatorScoreBreakdown, confidence: scoreConfidence } = await this.calculateOperatorScore();

    // Get ACWR confidence from recovery debt calculation
    let acwrDataSufficient = false;
    try {
      const { calculateRecoveryDebt: calcDebt } = await import('./recoveryDebt');
      const debtResult = await calcDebt();
      acwrDataSufficient = debtResult.acwrDataSufficient;
    } catch { /* non-critical */ }

    return {
      readiness,
      readinessIsProvisional,
      fitness,
      fatigue,
      recoveryDebt,
      weeklyVolume,
      weeklySessionCount,
      currentStreak: this.calculateStreak(),
      todaySubjective: todayRecovery ? {
        soreness: todayRecovery.overallSoreness ?? 5,
        stress: todayRecovery.stress ?? 5,
        energy: todayRecovery.energy ?? 5,
        motivation: todayRecovery.motivation ?? 5,
        sleepHours: todayRecovery.sleepHours ?? 0,
      } : null,
      programWeek,
      programProgress,
      operatorScore,
      operatorScoreBreakdown,
      dataConfidence: {
        bodyweightIsDefault: this.bodyweightIsDefault,
        acwrDataSufficient,
        ...scoreConfidence,
      },
      ...(this.todayActivity ? {
        neatScore: this.todayActivity.neatScore,
        activityZone: this.todayActivity.activityZone,
        stepsToday: this.todayActivity.steps,
        activeMinutesToday: this.todayActivity.activeMinutes,
      } : {}),
    };
  }

  /**
   * Calculate Operator Score (0-100)
   * NOW USES calculateBattleReadiness from operatorStandards.ts for consistency
   * This ensures Visual, AI Brief, and Detailed views ALL show the same score!
   *
   * OPTIMIZED: Uses pre-fetched data from constructor to avoid N+1 queries
   */
  private async calculateOperatorScore(): Promise<{
    totalScore: number;
    breakdown: { strength: number; endurance: number; bodyweight: number; recovery: number; consistency: number };
    confidence: { hasStrengthData: boolean; hasEnduranceData: boolean; hasBodyweightData: boolean };
  }> {
    // ========================================
    // DATA EXTRACTION (uses cached data from constructor)
    // ========================================

    // Use pre-fetched bodyweight
    const bodyweight = this.bodyweight;

    // Extract endurance times from cached assessments
    let assessmentRuckTime: number | null = null;
    let assessmentRunTime: number | null = null;

    if (this.assessments.length > 0) {
      const latest = this.assessments.sort((a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      )[0];

      if (latest.ruck6MileMinutes !== undefined && latest.ruck6MileSeconds !== undefined) {
        assessmentRuckTime = latest.ruck6MileMinutes * 60 + latest.ruck6MileSeconds;
      }
      if (latest.run1_5MileMinutes !== undefined && latest.run1_5MileSeconds !== undefined) {
        assessmentRunTime = latest.run1_5MileMinutes * 60 + latest.run1_5MileSeconds;
      }
    }

    // Use pre-fetched 1RMs (already loaded in constructor)
    const deadlift1RM = this.oneRMs.deadlift || this.oneRMs['Deadlift'] || 0;
    const squat1RM = this.oneRMs.backSquat || this.oneRMs['Back Squat'] || this.oneRMs.squat || 0;
    const bench1RM = this.oneRMs.benchPress || this.oneRMs['Bench Press'] || 0;

    // Extract bodyweight exercise maxes from sessions
    let maxPullUps = 0;
    let maxPushUps = 0;
    let maxDips = 0;
    let bestRuck6Mile: number | null = null;
    let bestRun1_5Mile: number | null = null;
    let conditioningSeconds7d = 0;
    let conditioningMeters7d = 0;
    
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    
    const parseTimeToSeconds = (timeStr: string): number | null => {
      const parts = timeStr.split(':').map(p => parseInt(p, 10));
      if (parts.length === 2 && parts.every(n => Number.isFinite(n))) return parts[0] * 60 + parts[1];
      if (parts.length === 3 && parts.every(n => Number.isFinite(n))) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      return null;
    };

    this.sessions.forEach(session => {
      if (session.sessionType === 'range' || !('exercises' in session)) return;
      const ptSession = session as PTSessionLog;
      const sessionDate = new Date(session.date);
      const isRecent = sessionDate >= weekAgo;
      
      ptSession.exercises.forEach((ex: any) => {
        const nameLower = ex.exerciseName.toLowerCase();

        // Pull-ups / chin-ups
        if ((nameLower.includes('pull') && nameLower.includes('up')) ||
            (nameLower.includes('chin') && nameLower.includes('up'))) {
          if (ex.sets && ex.sets.length > 0) {
            ex.sets.forEach((set: any) => {
              if (set.reps > maxPullUps) maxPullUps = set.reps;
            });
          } else if (ex.actualReps && ex.actualReps > maxPullUps) {
            maxPullUps = ex.actualReps;
          }
        }

        // Push-ups
        if (nameLower.includes('push') && nameLower.includes('up')) {
          if (ex.sets && ex.sets.length > 0) {
            ex.sets.forEach((set: any) => {
              if (set.reps > maxPushUps) maxPushUps = set.reps;
            });
          } else if (ex.actualReps && ex.actualReps > maxPushUps) {
            maxPushUps = ex.actualReps;
          }
        }

        // Dips
        if (nameLower.includes('dip') && !nameLower.includes('bench dip')) {
          if (ex.sets && ex.sets.length > 0) {
            ex.sets.forEach((set: any) => {
              if (set.reps > maxDips) maxDips = set.reps;
            });
          } else if (ex.actualReps && ex.actualReps > maxDips) {
            maxDips = ex.actualReps;
          }
        }

        // 6-mile ruck
        if (nameLower.includes('ruck') && (nameLower.includes('6 mile') || nameLower.includes('6-mile'))) {
          if (ex.actualTime) {
            const sec = parseTimeToSeconds(ex.actualTime);
            if (sec && (!bestRuck6Mile || sec < bestRuck6Mile)) {
              bestRuck6Mile = sec;
            }
          }
        }

        // 1.5-mile run
        if (nameLower.includes('1.5 mile run') || nameLower.includes('1.5-mile run')) {
          if (ex.actualTime) {
            const sec = parseTimeToSeconds(ex.actualTime);
            if (sec && (!bestRun1_5Mile || sec < bestRun1_5Mile)) {
              bestRun1_5Mile = sec;
            }
          }
        }

        // Conditioning load (last 7 days only)
        if (isRecent) {
          // Cardio/Ruck with time/distance
          if ((ex.exerciseType === 'cardio' || ex.exerciseType === 'ruck') && ex.actualTime) {
            const sec = parseTimeToSeconds(ex.actualTime);
            if (sec) conditioningSeconds7d += sec;
          }
          if ((ex.exerciseType === 'cardio' || ex.exerciseType === 'ruck') && ex.actualDistance && ex.actualDistance > 0) {
            conditioningMeters7d += ex.actualDistance;
          }

          // HIIT intervals & circuits
          if (ex.exerciseType === 'circuit' && ex.circuitSets && ex.circuitSets.length > 0) {
            const distances = ex.circuitSets
              .map((s: any) => s.distance)
              .filter((d: any) => typeof d === 'number' && d > 0) as number[];
            if (distances.length > 0) {
              conditioningMeters7d += distances.reduce((sum: number, d: number) => sum + d, 0);
            }

            const times = ex.circuitSets
              .map((s: any) => s.time)
              .filter((t: any) => typeof t === 'string' && t.trim() !== '') as string[];
            if (times.length > 0) {
              times.forEach(t => {
                const sec = parseTimeToSeconds(t);
                if (sec) conditioningSeconds7d += sec;
              });
            } else if (distances.length > 0) {
              conditioningSeconds7d += 30 * distances.length; // Estimate 30s per interval
            }
          }
        }
      });
    });

    // Use assessment times if available, otherwise use session times
    const finalRuckTime = assessmentRuckTime || bestRuck6Mile;
    const finalRunTime = assessmentRunTime || bestRun1_5Mile;

    // Calculate recovery metrics
    const last7Recovery = this.recoveryData.slice(-7);
    const avgReadiness = last7Recovery.length > 0
      ? last7Recovery.reduce((sum, r) => sum + (r?.readinessScore || 0), 0) / last7Recovery.length
      : this.profile?.recoveryProfile.baselineReadiness || 70;

    // Calculate streak
    const streak = this.calculateStreak();

    // Workouts this week
    const workoutsThisWeek = this.sessions.filter(s => {
      const sessionDate = new Date(s.date);
      const hasExercises = s.sessionType === 'range' ? true : (s as any).exercises && (s as any).exercises.length > 0;
      return sessionDate >= weekAgo && hasExercises;
    }).length;

    // Days since last training session — feeds the detraining decay applied
    // inside calculateBattleReadiness. A user who hasn't trained in 30+ days
    // shouldn't see pre-break 1RM-derived numbers next to "X days off the
    // books." Recovery + consistency pillars already reflect now-state.
    let daysSinceLastSession = Infinity;
    if (this.sessions.length > 0) {
      const ptSessions = this.sessions.filter((s: any) =>
        s.sessionType === 'pt' || s.sessionType === 'assessment'
      );
      if (ptSessions.length > 0) {
        const latestDate = ptSessions
          .map((s: any) => new Date(s.date).getTime())
          .filter((t: number) => !isNaN(t))
          .sort((a: number, b: number) => b - a)[0];
        if (latestDate) {
          daysSinceLastSession = Math.max(0, Math.floor((Date.now() - latestDate) / 86_400_000));
        }
      }
    }

    // ========================================
    // USE SHARED CALCULATION (operatorStandards.ts)
    // ========================================
    const battleReadiness = calculateBattleReadiness({
      bodyweight,
      deadlift1RM,
      squat1RM,
      bench1RM,
      ruck6MileTime: finalRuckTime,
      run1_5MileTime: finalRunTime,
      conditioningSeconds7d,
      conditioningMeters7d,
      pullUpsMax: maxPullUps,
      pushUpsMax: maxPushUps,
      dipsMax: maxDips,
      readinessScore: avgReadiness,
      currentStreak: streak,
      workoutsThisWeek,
      daysSinceLastSession: Number.isFinite(daysSinceLastSession) ? daysSinceLastSession : undefined,
    });

    // Debug logging for operator score calculation
    console.log('� Operator Score Calculation (UNIFIED):', {
      dataSource: {
        sessions: `${this.sessions.length} sessions from Dexie`,
        recovery: `${this.recoveryData.length} recovery entries from Dexie`,
        oneRMs: 'localStorage (oneRMs)',
        assessments: 'localStorage (fitnessTests)',
      },
      inputs: {
        bodyweight,
        oneRMs: { deadlift: deadlift1RM, squat: squat1RM, bench: bench1RM },
        enduranceTimes: { ruck: finalRuckTime, run: finalRunTime },
        conditioning: { seconds7d: conditioningSeconds7d, meters7d: conditioningMeters7d },
        bodyweightMaxes: { pullUps: maxPullUps, pushUps: maxPushUps, dips: maxDips },
        recovery: avgReadiness,
        consistency: { streak, workoutsThisWeek },
      },
      output: {
        finalScore: battleReadiness.score,
        breakdown: battleReadiness.breakdown,
      },
    });

    return {
      totalScore: battleReadiness.score,
      breakdown: battleReadiness.breakdown,
      confidence: {
        hasStrengthData: deadlift1RM > 0 || squat1RM > 0 || bench1RM > 0,
        hasEnduranceData: !!finalRuckTime || !!finalRunTime || conditioningSeconds7d > 0 || conditioningMeters7d > 0,
        hasBodyweightData: maxPullUps > 0 || maxPushUps > 0,
      },
    };
  }

  // NOTE: calculateEnduranceScore, getBestEnduranceTimes, getConditioningLoad, and getBodyweightMaxes
  // have been REMOVED. All data extraction is now done in calculateOperatorScore() which uses
  // the shared calculateBattleReadiness() function from operatorStandards.ts
  // This ensures CONSISTENCY across all dashboard views (Visual, AI Brief, Detailed)

  private calculateStreak(): number {
    if (this.sessions.length === 0) return 0;

    // Only count actual training sessions (pt, assessment, range, sprint)
    // Filter out any rest/recovery-only sessions
    const trainingSessions = this.sessions.filter(s => 
      s.sessionType === 'pt' || s.sessionType === 'assessment' || 
      s.sessionType === 'range' || s.sessionType === 'sprint'
    );

    if (trainingSessions.length === 0) return 0;

    const sorted = [...trainingSessions].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    let streak = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    for (const session of sorted) {
      const sessionDate = new Date(session.date);
      sessionDate.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor(
        (currentDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      // Allow 1-day gaps for rest days
      // If we're within 2 days (0, 1, or 2), continue the streak
      if (daysDiff <= streak + 1) {
        if (daysDiff === streak || daysDiff === streak + 1) {
          streak = daysDiff + 1;
          currentDate = new Date(sessionDate);
          currentDate.setDate(currentDate.getDate() - 1);
        }
      } else {
        break;
      }
    }

    return streak;
  }

  /**
   * Analyze patterns (what we've LEARNED)
   */
  private analyzePatterns() {
    if (!this.profile) {
      return {
        recoveryCapacity: 'average' as const,
        recoverySpeed: 'average' as const,
        rpeAccuracy: 'accurate' as const,
        progressionRate: 0.025,
        optimalFrequency: 3,
        optimalRestDays: 1.5,
      };
    }

    return {
      recoveryCapacity: this.profile.recoveryProfile.capacity,
      recoverySpeed: this.profile.recoveryProfile.speed,
      rpeAccuracy: this.profile.rpeCalibration.tendency,
      progressionRate: this.profile.progressionProfile.preferredProgressionRate,
      optimalFrequency: this.profile.progressionProfile.optimalFrequency,
      optimalRestDays: this.profile.recoveryProfile.optimalRestDays,
    };
  }

  /**
   * Generate predictions (what's COMING)
   */
  private async generatePredictions() {
    const currentState = await this.analyzeCurrentState();
    const patterns = this.analyzePatterns();

    // Predict next session performance
    const nextSessionPerformance = await this.predictNextSession(currentState, patterns);

    // Predict deload need
    const deloadRecommendation = this.predictDeload(currentState, patterns);

    // Predict injury risk
    const injuryRisk = await this.predictInjuryRisk(currentState, patterns);

    // Detect plateaus
    const plateaus = await detectPlateaus(this.sessions);
    const plateauWarnings = plateaus
      .filter(p => p.plateauDetected)
      .map(p => ({
        exercise: p.exerciseName,
        weeksSinceProgress: p.weeksSinceProgress,
        suggestedIntervention: p.interventions[0]?.description || 'Increase volume or intensity',
      }));

    return {
      nextSessionPerformance,
      deloadRecommendation,
      injuryRisk,
      plateauWarnings,
    };
  }

  /**
   * Generate natural language insights — data-driven, specific, actionable
   */
  private generateInsights(
    currentState: any,
    patterns: any,
    predictions: any,
    progressions?: IntelligenceReport['progressions'],
    extended?: ExtendedIntelligenceData,
    trends?: IntelligenceReport['trends']
  ) {
    const insights: string[] = [];
    const warnings: string[] = [];
    const wins: string[] = [];

    // ── PRIMARY INSIGHT: compound multiple signals into one sentence ──
    const parts: string[] = [];
    parts.push(`Readiness ${currentState.readiness}/100`);
    if (extended?.muscleStatus?.overallACWR) {
      const acwr = extended.muscleStatus.overallACWR;
      const acwrWord = acwr > 1.5 ? 'spiking' : acwr > 1.3 ? 'elevated' : acwr >= 0.8 ? 'optimal' : 'low';
      parts.push(`ACWR ${acwr.toFixed(2)} (${acwrWord})`);
    }
    if (currentState.fatigue > 60) parts.push(`fatigue ${Math.round(currentState.fatigue)}`);

    let primaryVerdict = '';
    if (predictions.deloadRecommendation.urgency === 'high' || predictions.deloadRecommendation.urgency === 'critical') {
      primaryVerdict = 'Deload needed — accumulated fatigue is outpacing recovery.';
    } else if (currentState.readiness >= 80 && currentState.fatigue < 50) {
      primaryVerdict = 'Peak conditions — push hard today.';
    } else if (currentState.readiness >= 60) {
      primaryVerdict = 'Good to train — standard intensity.';
    } else if (currentState.readiness >= 40) {
      primaryVerdict = 'Reduce intensity — recovery is lagging.';
    } else {
      primaryVerdict = 'Rest day recommended — readiness is critically low.';
    }
    const primary = `${parts.join(' · ')}. ${primaryVerdict}`;

    // ── WARNINGS: specific, data-backed ──

    // Deload
    if (predictions.deloadRecommendation.urgency === 'high' || predictions.deloadRecommendation.urgency === 'critical') {
      warnings.push(predictions.deloadRecommendation.reasoning);
    }

    // Injury risk with specific factors
    if (predictions.injuryRisk.level === 'high' || predictions.injuryRisk.level === 'critical') {
      const factors = predictions.injuryRisk.factors.length > 0
        ? predictions.injuryRisk.factors.join(', ')
        : 'accumulated load';
      warnings.push(`Injury risk ${predictions.injuryRisk.level}: ${factors}.`);
    }

    // Specific plateau warnings (name exercises, not just count)
    for (const pw of predictions.plateauWarnings.slice(0, 2)) {
      warnings.push(`${pw.exercise} stalled ${pw.weeksSinceProgress} weeks — ${pw.suggestedIntervention}.`);
    }

    // ACWR overload warnings (high ACWR only — undertrained muscles aren't
    // a "warning," they're a rebuild prompt that lives elsewhere).
    if (extended?.muscleStatus?.byMuscleGroup) {
      const overloadedMuscles = Object.entries(extended.muscleStatus.byMuscleGroup)
        .filter(([, v]) => v.riskLevel === 'danger' && v.acwr > 1.3)
        .sort(([, a], [, b]) => b.acwr - a.acwr);
      for (const [name, data] of overloadedMuscles.slice(0, 2)) {
        warnings.push(`${name} ACWR at ${data.acwr.toFixed(1)} (overload) — ${data.recommendation}.`);
      }
    }

    // NEAT suppression
    if (this.neatSuppression?.detected) {
      warnings.push(
        `NEAT suppressed ${Math.round(this.neatSuppression.dropPercent)}% below baseline — early overtraining marker.`
      );
    }

    // Biometrics: elevated RHR or suppressed HRV
    if (extended?.biometrics) {
      const bio = extended.biometrics;
      if (bio.rhrStatus === 'elevated' && bio.rhrCurrent && bio.rhrBaseline) {
        warnings.push(`RHR elevated at ${bio.rhrCurrent} bpm (baseline ${bio.rhrBaseline}) — stress or insufficient recovery.`);
      }
      if (bio.hrvStatus === 'suppressed' && bio.hrvCurrent && bio.hrvBaseline) {
        warnings.push(`HRV suppressed at ${bio.hrvCurrent} ms (baseline ${bio.hrvBaseline}) — autonomic recovery lagging.`);
      }
      if (bio.sleepMinutes != null && bio.sleepMinutes < 390) {
        warnings.push(`Sleep at ${(bio.sleepMinutes / 60).toFixed(1)} hours — aim for 7+ hours, especially before hard sessions.`);
      }
    }

    // Poor exercise efficiency
    const worstExercise = extended?.exerciseEfficiency?.worstExercises?.[0];
    if (worstExercise && worstExercise.sfr < 0.5) {
      warnings.push(`${worstExercise.name} has a poor stimulus:fatigue ratio (${worstExercise.sfr.toFixed(2)}). Consider swapping for a more efficient movement.`);
    }

    // Nutrition warnings
    if (this.nutritionData && this.nutritionData.daysTracked > 0) {
      const nd = this.nutritionData;
      if (nd.proteinPerLbBodyweight !== null && nd.proteinPerLbBodyweight < 0.7) {
        warnings.push(
          `Protein at ${nd.proteinPerLbBodyweight.toFixed(2)} g/lb — below minimum for recovery. Target ${Math.round(this.bodyweight * 0.8)}g+ daily.`
        );
      }
      if (nd.avgCalorieAdherence < 75) {
        warnings.push(
          `Calorie adherence ${nd.avgCalorieAdherence}% over ${nd.daysTracked} days — under-fueling impairs recovery and strength gains.`
        );
      }
      if (nd.avgCalorieBalance < -500) {
        warnings.push(
          `Running ${Math.abs(Math.round(nd.avgCalorieBalance))} kcal/day deficit — this magnitude limits muscle recovery and endurance.`
        );
      }
    }

    // Failing assessment tests
    if (extended?.assessmentGrades?.testGrades) {
      const failing = extended.assessmentGrades.testGrades.filter(t => !t.meetsMinimum);
      if (failing.length > 0) {
        warnings.push(
          `Below minimum on: ${failing.map(t => `${t.testName} (${t.grade})`).join(', ')}.`
        );
      }
    }

    // Volume status
    if (extended?.volumeStatus) {
      const vs = extended.volumeStatus;
      if (vs.muscleIssues.length > 0) {
        const topIssue = vs.muscleIssues[0];
        if (topIssue.adjustmentNeeded > 0) {
          insights.push(`${topIssue.muscle} is undertrained — add ${topIssue.adjustmentNeeded} sets/week for better balance.`);
        } else if (topIssue.adjustmentNeeded < 0) {
          insights.push(`${topIssue.muscle} volume exceeds MRV — cut ${Math.abs(topIssue.adjustmentNeeded)} sets/week to avoid junk volume.`);
        }
      }
    }

    // ── SECONDARY INSIGHTS: specific, informative ──

    // Session frequency
    if (currentState.weeklySessionCount >= patterns.optimalFrequency) {
      insights.push(`${currentState.weeklySessionCount}/${Math.round(patterns.optimalFrequency)} sessions done — weekly frequency target met.`);
    } else if (currentState.weeklySessionCount > 0) {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysLeft = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      const needed = Math.round(patterns.optimalFrequency) - currentState.weeklySessionCount;
      if (daysLeft > 0) {
        insights.push(`${currentState.weeklySessionCount}/${Math.round(patterns.optimalFrequency)} sessions — ${needed} more needed with ${daysLeft} days left.`);
      }
    }

    // Volume trends.
    //
    // Gated on isWeekComparable for the same reason the Weekly Pulse and
    // "This Week vs Last" tiles are: early in the week the cumulative total
    // is near zero against a complete previous day, so this fired as
    // "Volume down 11,685 lbs vs same point last week." on a Monday morning
    // for an athlete who simply had not trained yet.
    //
    // This was the surface that mattered most to gate. The tiles at least
    // render the artefact as a small delta chip next to the real value; here
    // it became a full declarative SENTENCE in the Intel Briefing, sitting a
    // few hundred pixels from a Weekly Pulse tile that (correctly) said
    // "so far" — two systems making opposite claims about one number.
    if (trends?.weekToDate && trends.weekToDateDelta && isWeekComparable()) {
      const tonDelta = trends.weekToDateDelta.strengthTonnageLbs;
      if (Math.abs(tonDelta) > 1000) {
        const dir = tonDelta > 0 ? 'up' : 'down';
        insights.push(
          `Volume ${dir} ${Math.abs(Math.round(tonDelta)).toLocaleString()} lbs vs same point last week.`
        );
      }
    }

    // Exercises ready for progression
    if (progressions?.weights.ready && progressions.weights.ready.length > 0) {
      const top = progressions.weights.ready.slice(0, 2);
      insights.push(
        `Ready to progress: ${top.map(l => `${l.exerciseName} → ${l.suggestedWeight} lbs`).join(', ')}.`
      );
    }

    // Daily activity
    if (this.todayActivity) {
      if (this.todayActivity.activityZone === 'active' || this.todayActivity.activityZone === 'highly_active') {
        insights.push(`${this.todayActivity.steps.toLocaleString()} steps today — solid daily movement.`);
      } else if (this.todayActivity.activityZone === 'sedentary') {
        insights.push(`Low daily movement (${this.todayActivity.steps.toLocaleString()} steps). Even light walking aids recovery.`);
      }
    }

    // ── WINS: celebrate real achievements ──

    // Weekly volume
    if (currentState.weeklyVolume > 0) {
      wins.push(`${Math.round(currentState.weeklyVolume).toLocaleString()} lbs moved this week.`);
    }

    // Ruck miles
    if (trends?.weekToDate?.ruckMiles && trends.weekToDate.ruckMiles > 0) {
      wins.push(`${trends.weekToDate.ruckMiles.toFixed(1)} ruck miles this week.`);
    }

    // Operator Score trending up
    if (trends?.operatorScoreDelta7d && trends.operatorScoreDelta7d > 0) {
      wins.push(`Operator Score up ${trends.operatorScoreDelta7d > 0 ? '+' : ''}${Math.round(trends.operatorScoreDelta7d)} points in the last 7 days.`);
    }

    // Recovery capacity
    if (patterns.recoveryCapacity === 'high') {
      wins.push('Recovery capacity rated high — you can handle more volume than average.');
    }

    // Efficient exercises
    const bestExercise = extended?.exerciseEfficiency?.bestExercises?.[0];
    if (bestExercise && bestExercise.sfr > 1.2) {
      wins.push(`${bestExercise.name} is your most efficient exercise (SFR ${bestExercise.sfr.toFixed(2)}) — high stimulus, low fatigue.`);
    }

    // Nutrition wins
    if (this.nutritionData && this.nutritionData.daysTracked > 0) {
      const nd = this.nutritionData;
      if (nd.streak >= 5) {
        wins.push(`${nd.streak}-day nutrition tracking streak.`);
      }
      if (nd.proteinPerLbBodyweight !== null && nd.proteinPerLbBodyweight >= 0.8 && nd.proteinPerLbBodyweight <= 1.2) {
        wins.push(`Protein at ${nd.proteinPerLbBodyweight.toFixed(2)} g/lb — optimal range.`);
      }
    }

    // Assessment wins
    if (extended?.assessmentGrades?.testGrades) {
      const passing = extended.assessmentGrades.testGrades.filter(t => t.meetsMinimum && t.score >= 80);
      if (passing.length > 0) {
        wins.push(`Scoring ${passing.length} assessment test${passing.length > 1 ? 's' : ''} at 80%+.`);
      }
    }

    // Body composition progress
    if (extended?.bodyComposition?.hasData) {
      const bc = extended.bodyComposition;
      if (bc.bodyFatDeltaPercent != null && bc.bodyFatDeltaPercent < -1) {
        wins.push(`Body fat down ${Math.abs(bc.bodyFatDeltaPercent).toFixed(1)}% from baseline.`);
      }
      if (bc.weightDeltaKg != null && bc.leanMassLbs != null && bc.weightDeltaKg > 0 && bc.bodyFatDeltaPercent != null && bc.bodyFatDeltaPercent <= 0) {
        wins.push('Gaining weight while maintaining or dropping body fat — lean mass increasing.');
      }
    }

    return {
      primary,
      primaryVerdict,
      secondary: insights,
      warnings,
      wins,
    };
  }

  /**
   * Generate actionable recommendations — data-driven, specific, backed by real metrics
   */
  private generateRecommendations(
    currentState: any,
    patterns: any,
    predictions: any,
    progressions?: IntelligenceReport['progressions'],
    extended?: ExtendedIntelligenceData
  ) {
    // ── TODAY'S RECOMMENDATION: specific actions, not generic intensity labels ──
    let todayAction = '';
    let todayReasoning = '';
    let todayIntensity: 'light' | 'moderate' | 'hard' | 'max' = 'moderate';

    const hasModerateOrSevereSuppression =
      this.neatSuppression?.detected &&
      (this.neatSuppression.severity === 'moderate' || this.neatSuppression.severity === 'severe');

    // Gather specifics for today's action
    const readyLifts = progressions?.weights.ready.slice(0, 2) || [];
    const readyIntervals = progressions?.intervals.ready.slice(0, 1) || [];
    // Only OVERLOADED muscles (acwr > 1.3) belong in "ease off X" copy.
    // Undertrained muscles also get classified as 'danger' by the ACWR
    // engine but the action there is to *add* volume, not avoid them.
    const dangerMuscles = extended?.muscleStatus?.byMuscleGroup
      ? Object.entries(extended.muscleStatus.byMuscleGroup)
          .filter(([, v]) => v.riskLevel === 'danger' && v.acwr > 1.3)
          .map(([name]) => name)
      : [];
    const overallACWR = extended?.muscleStatus?.overallACWR;

    // REENTRY CASE — runs before all readiness branches because none of them
    // are correct after a 14+ day break. Readiness can read "70" simply
    // because no fatigue has accumulated; that doesn't mean "go hard."
    const reentryStatusLocal = (currentState as any).reentryStatus as string | undefined;
    const reentryDaysLocal = (currentState as any).reentryDays as number | undefined;
    // PROVISIONAL READINESS — leads the chain (before reentry/deload/readiness)
    // so none of those overwrite it. When readiness is the baseline constant with
    // nothing behind it (no recovery ever logged), the >=70 branch would tell a
    // brand-new user to "push working sets at RPE 7-8" off a fabricated number —
    // the exact contradiction the audit caught (green "RDY 70 / train hard" above
    // a card that says "awaiting first session"). Recommend a calibrating first
    // session instead of an intensity.
    if (currentState.readinessIsProvisional) {
      todayIntensity = 'moderate';
      todayAction = 'Log your first session at a comfortable effort — the app calibrates your loads and readiness from real sets, not a starting guess.';
      todayReasoning = 'No training or recovery history yet, so today\'s readiness is a placeholder, not a measurement. One logged session starts the real signal.';
    } else if (reentryStatusLocal === 'returning') {
      todayIntensity = 'light';
      const days = reentryDaysLocal ?? 14;
      const cap = days < 21 ? 70 : days < 35 ? 60 : 55;
      todayAction = `Comeback session: cap working sets at ${cap}% of pre-break load. Hit form, not numbers. ${days} days off the books.`;
      todayReasoning = `${days} days since last session — readiness number is misleading after a break. Easing back in protects the next 4 weeks of training.`;
    } else if (reentryStatusLocal === 'drifting') {
      // Still salvageable as a normal session, but bias to the moderate end
      // and explicitly mention the gap.
      todayIntensity = 'moderate';
      const days = reentryDaysLocal ?? 7;
      todayAction = `Light-to-moderate session — ${days} days off. One short workout this week keeps the streak alive; no need to swing for the fences.`;
      todayReasoning = `${days} days since last session. The reasonable goal is consistency restoration, not progressive overload.`;
    } else if (predictions.deloadRecommendation.urgency === 'high' || predictions.deloadRecommendation.urgency === 'critical') {
      todayIntensity = 'light';
      if (overallACWR && overallACWR > 1.3) {
        todayAction = `Deload: cut volume 40-50%, cap RPE at 6. ACWR is ${overallACWR.toFixed(2)}.`;
      } else {
        todayAction = 'Deload: reduce all working weights 40-50%, stop 2 reps short of failure.';
      }
      if (dangerMuscles.length > 0) {
        todayAction += ` Skip direct ${dangerMuscles.slice(0, 2).join(' and ')} work.`;
      }
      todayReasoning = predictions.deloadRecommendation.reasoning;
    } else if (currentState.readiness >= 80 && currentState.fatigue < 60) {
      if (hasModerateOrSevereSuppression) {
        todayIntensity = 'moderate';
        todayAction = 'Moderate intensity — NEAT suppression signals hidden fatigue despite good readiness.';
        todayReasoning = `Daily movement dropped ${Math.round(this.neatSuppression!.dropPercent)}% below baseline. Avoid max effort.`;
      } else {
        todayIntensity = 'max';
        if (readyLifts.length > 0) {
          todayAction = `Push hard. Try ${readyLifts.map(l => `${l.exerciseName} at ${l.suggestedWeight} lbs`).join(', ')}.`;
          todayReasoning = `Readiness ${currentState.readiness}, fatigue ${Math.round(currentState.fatigue)}. ${readyLifts[0].reason}.`;
        } else {
          todayAction = 'Peak conditions for high-intensity work or PR attempts.';
          todayReasoning = `Readiness ${currentState.readiness}/100, fatigue only ${Math.round(currentState.fatigue)}.`;
        }
      }
    } else if (currentState.readiness >= 70) {
      todayIntensity = 'hard';
      const actions: string[] = [];
      if (readyLifts.length > 0) {
        actions.push(`Progress ${readyLifts[0].exerciseName} to ${readyLifts[0].suggestedWeight} lbs`);
      }
      if (readyIntervals.length > 0) {
        actions.push(`${readyIntervals[0].exerciseName}: ${readyIntervals[0].suggestedChange}`);
      }
      if (dangerMuscles.length > 0) {
        actions.push(`ease off ${dangerMuscles[0]}`);
      }
      todayAction = actions.length > 0
        ? `Standard session. ${actions.join('. ')}.`
        : 'Good day for progressive overload — push working sets at RPE 7-8.';
      todayReasoning = `Readiness ${currentState.readiness}/100 — solid training conditions.`;
    } else if (currentState.readiness >= 50) {
      todayIntensity = 'moderate';
      if (dangerMuscles.length > 0) {
        todayAction = `Moderate intensity. Avoid ${dangerMuscles.join(', ')} — ACWR elevated. Focus on technique work.`;
      } else {
        todayAction = 'Moderate intensity — maintain volume but cap RPE at 7. Focus on form.';
      }
      todayReasoning = `Readiness ${currentState.readiness}/100 — adequate but not optimal. Don't dig a recovery hole.`;
    } else {
      todayIntensity = 'light';
      const bio = extended?.biometrics;
      const reasons: string[] = [`readiness ${currentState.readiness}/100`];
      if (bio?.rhrStatus === 'elevated') reasons.push('RHR elevated');
      if (bio?.hrvStatus === 'suppressed') reasons.push('HRV suppressed');
      if (bio?.sleepMinutes != null && bio.sleepMinutes < 390) reasons.push(`sleep ${(bio.sleepMinutes / 60).toFixed(1)}h`);
      todayAction = 'Active recovery or rest. Zone 2 cardio or mobility work only.';
      todayReasoning = `Low recovery state: ${reasons.join(', ')}. Training hard today delays your rebound.`;
    }

    // ── THIS WEEK'S FOCUS: data-driven adjustments ──
    let weekFocus = '';
    const weekAdjustments: string[] = [];

    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysLeft = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const sessionsThisWeek = currentState.weeklySessionCount;
    const targetSessions = Math.round(patterns.optimalFrequency);
    const sessionsNeeded = Math.max(0, targetSessions - sessionsThisWeek);

    // Rank pillars for weakness-aware focus
    const pillarMax: Record<string, number> = { strength: 30, endurance: 25, bodyweight: 20, recovery: 15, consistency: 10 };
    const breakdown = currentState.operatorScoreBreakdown as Record<string, number> | undefined;
    let weakestPillar = '';
    let weakestNorm = 1;
    if (breakdown) {
      for (const [pillar, max] of Object.entries(pillarMax)) {
        const norm = (breakdown[pillar] ?? 0) / max;
        if (norm < weakestNorm) {
          weakestNorm = norm;
          weakestPillar = pillar;
        }
      }
    }

    if (predictions.deloadRecommendation.needed) {
      weekFocus = 'Deload — reduce fatigue before it forces a break';
      if (overallACWR && overallACWR > 1.3) {
        weekAdjustments.push(`ACWR at ${overallACWR.toFixed(2)} — cut volume 40-50% until it drops below 1.3.`);
      } else {
        weekAdjustments.push('Cut all working weights 40-50% and cap RPE at 6.');
      }
      if (dangerMuscles.length > 0) {
        weekAdjustments.push(`Skip direct ${dangerMuscles.join(', ')} work for 48-72 hours.`);
      }
      weekAdjustments.push('Keep conditioning to Zone 2 only.');
    } else if (sessionsThisWeek === 0 && daysLeft > 0) {
      weekFocus = `${daysLeft} days left — get ${Math.min(targetSessions, daysLeft)} sessions in`;
      weekAdjustments.push('Even a 20-minute session counts toward consistency.');
      if (weakestPillar && weakestNorm < 0.6) {
        const label = weakestPillar.charAt(0).toUpperCase() + weakestPillar.slice(1);
        weekAdjustments.push(`Focus on ${label} — it's your biggest Operator Score gap.`);
      }
    } else if (sessionsThisWeek >= targetSessions) {
      weekFocus = `${sessionsThisWeek}/${targetSessions} sessions — target met`;
      weekAdjustments.push(`${Math.round(currentState.weeklyVolume).toLocaleString()} lbs moved this week.`);
      if (currentState.readiness >= 70) {
        weekAdjustments.push('Bonus session optional — recovery can handle it.');
      } else {
        weekAdjustments.push('Protect recovery — you\'ve earned the rest days.');
      }
    } else {
      weekFocus = `${sessionsNeeded} more sessions needed (${sessionsThisWeek}/${targetSessions})`;
      if (sessionsNeeded > daysLeft && daysLeft > 0) {
        weekAdjustments.push(`Behind pace — ${sessionsNeeded} sessions in ${daysLeft} days. Consider a back-to-back.`);
      }
    }

    // Tactical adjustments from real data
    const topPlateau = predictions.plateauWarnings?.[0];
    if (topPlateau) {
      weekAdjustments.push(`${topPlateau.exercise} stalled ${topPlateau.weeksSinceProgress} weeks — ${topPlateau.suggestedIntervention}.`);
    }

    if (readyLifts.length > 0 && !predictions.deloadRecommendation.needed) {
      weekAdjustments.push(`Progress: ${readyLifts.map(l => `${l.exerciseName} → ${l.suggestedWeight} lbs`).join(', ')}.`);
    }

    if (readyIntervals.length > 0) {
      weekAdjustments.push(`${readyIntervals[0].exerciseName}: ${readyIntervals[0].suggestedChange}.`);
    }

    // Volume imbalances
    const muscleIssue = extended?.volumeStatus?.muscleIssues[0];
    if (muscleIssue && muscleIssue.adjustmentNeeded !== 0) {
      const dir = muscleIssue.adjustmentNeeded > 0 ? 'Add' : 'Cut';
      weekAdjustments.push(`${muscleIssue.muscle} ${muscleIssue.status} — ${dir} ${Math.abs(muscleIssue.adjustmentNeeded)} sets/week.`);
    }

    // ACWR risk
    if (!predictions.deloadRecommendation.needed) {
      const cautionMuscles = extended?.muscleStatus?.byMuscleGroup
        ? Object.entries(extended.muscleStatus.byMuscleGroup)
            .filter(([, v]) => v.riskLevel === 'danger' || v.riskLevel === 'caution')
            .sort(([, a], [, b]) => b.acwr - a.acwr)
            .slice(0, 1)
        : [];
      if (cautionMuscles.length > 0) {
        const [name, data] = cautionMuscles[0];
        weekAdjustments.push(`${name} ACWR ${data.acwr.toFixed(1)} (${data.riskLevel}) — ${data.recommendation}.`);
      }
    }

    // Failing assessment
    const failingTest = extended?.assessmentGrades?.testGrades?.find(t => !t.meetsMinimum);
    if (failingTest) {
      weekAdjustments.push(`${failingTest.testName} below minimum (${failingTest.grade}) — add targeted work.`);
    }

    // Nutrition adjustments
    if (this.nutritionData && this.nutritionData.daysTracked > 0) {
      const nd = this.nutritionData;
      if (nd.proteinPerLbBodyweight !== null && nd.proteinPerLbBodyweight < 0.7) {
        weekAdjustments.push(`Protein at ${nd.proteinPerLbBodyweight.toFixed(1)} g/lb — target ${Math.round(this.bodyweight * 0.8)}g+/day.`);
      }
      if (nd.avgCalorieBalance < -500) {
        weekAdjustments.push(`${Math.abs(Math.round(nd.avgCalorieBalance))} kcal/day deficit — eat at maintenance on hard training days.`);
      } else if (nd.avgCalorieBalance > 500) {
        weekAdjustments.push(`+${Math.round(nd.avgCalorieBalance)} kcal/day surplus — monitor body composition.`);
      }
    }

    // Weakest pillar (if not already covered above)
    if (weakestPillar && weakestNorm < 0.5 && !predictions.deloadRecommendation.needed) {
      const label = weakestPillar.charAt(0).toUpperCase() + weakestPillar.slice(1);
      const score = breakdown ? Math.round(breakdown[weakestPillar]) : 0;
      const max = pillarMax[weakestPillar];
      weekAdjustments.push(`${label} is weakest (${score}/${max}) — highest-leverage area for Operator Score gains.`);
    }

    // ── NEXT WEEK: specific, data-driven outlook ──
    let nextWeekOutlook = '';
    const nextWeekPrep: string[] = [];

    if (predictions.deloadRecommendation.needed && predictions.deloadRecommendation.urgency !== 'low') {
      nextWeekOutlook = 'Recovery phase — rebuild readiness';
      nextWeekPrep.push('Keep all RPE at 6 or below.');
      if (extended?.biometrics?.sleepMinutes != null && extended.biometrics.sleepMinutes < 420) {
        nextWeekPrep.push(`Extend sleep — currently ${(extended.biometrics.sleepMinutes / 60).toFixed(1)} hours, aim for 7.5+.`);
      } else {
        nextWeekPrep.push('Prioritize 7.5+ hours of sleep per night.');
      }
      if (this.nutritionData?.proteinPerLbBodyweight != null && this.nutritionData.proteinPerLbBodyweight < 0.8) {
        nextWeekPrep.push(`Increase protein to ${Math.round(this.bodyweight * 0.8)}g+/day — critical for recovery.`);
      }
    } else if (currentState.fatigue > 70) {
      nextWeekOutlook = 'Fatigue management — prevent forced deload';
      nextWeekPrep.push('Plan 2 rest days between hard sessions.');
      if (dangerMuscles.length > 0) {
        nextWeekPrep.push(`Continue monitoring ${dangerMuscles.join(', ')} — ACWR needs to come down.`);
      }
      if (extended?.biometrics?.rhrStatus === 'elevated') {
        nextWeekPrep.push('Track RHR trend — sustained elevation means you need more recovery.');
      }
    } else if (currentState.fitness > 70 && currentState.fatigue < 50) {
      nextWeekOutlook = 'Green light for progression';
      if (readyLifts.length > 0) {
        nextWeekPrep.push(`Push ${readyLifts.map(l => l.exerciseName).join(', ')} at new weights.`);
      } else {
        nextWeekPrep.push('Add 1 set per major compound movement.');
      }
      if (this.nutritionData?.proteinPerLbBodyweight != null && this.nutritionData.proteinPerLbBodyweight >= 0.8) {
        nextWeekPrep.push('Nutrition is supporting your progress — maintain current intake.');
      } else if (this.nutritionData?.proteinPerLbBodyweight != null) {
        nextWeekPrep.push(`Increase protein to 0.8+ g/lb to maximize the progression window.`);
      }
      if (readyIntervals.length > 0) {
        nextWeekPrep.push(`Upgrade intervals: ${readyIntervals[0].suggestedChange}.`);
      }
    } else if (patterns.recoveryCapacity === 'high') {
      nextWeekOutlook = 'Volume increase opportunity';
      if (extended?.volumeStatus) {
        const vs = extended.volumeStatus;
        const underMuscles = vs.muscleIssues.filter(m => m.adjustmentNeeded > 0).slice(0, 2);
        if (underMuscles.length > 0) {
          nextWeekPrep.push(`Add sets to: ${underMuscles.map(m => `${m.muscle} (+${m.adjustmentNeeded})`).join(', ')}.`);
        } else {
          nextWeekPrep.push('Recovery capacity is high — add 1-2 sets to lagging muscle groups.');
        }
      }
      nextWeekPrep.push('Keep RPE in the 7-8.5 range for maximal adaptation.');
    } else if (patterns.recoveryCapacity === 'low') {
      nextWeekOutlook = 'Quality over quantity — protect recovery';
      nextWeekPrep.push('Cap at 3-4 sessions with 48+ hours between hard days.');
      const worstSFR = extended?.exerciseEfficiency?.worstExercises?.[0];
      if (worstSFR && worstSFR.sfr < 0.6) {
        nextWeekPrep.push(`Swap ${worstSFR.name} (SFR ${worstSFR.sfr.toFixed(2)}) for a more efficient movement.`);
      }
    } else {
      // Steady state — still provide specific guidance
      nextWeekOutlook = 'Maintain and build';
      if (weakestPillar && weakestNorm < 0.6) {
        const label = weakestPillar.charAt(0).toUpperCase() + weakestPillar.slice(1);
        nextWeekPrep.push(`Lean into ${label} training — biggest opportunity for score improvement.`);
      }
      if (progressions?.cardio?.hasData && progressions.cardio.recommendations.length > 0) {
        nextWeekPrep.push(progressions.cardio.recommendations[0]);
      }
      if (failingTest) {
        nextWeekPrep.push(`Continue building toward ${failingTest.testName} minimum standard.`);
      }
      if (nextWeekPrep.length === 0) {
        nextWeekPrep.push(`Target ${targetSessions} sessions with progressive overload on compound lifts.`);
      }
    }

    return {
      today: {
        action: todayAction,
        reasoning: todayReasoning,
        intensity: todayIntensity,
      },
      thisWeek: {
        focus: weekFocus,
        adjustments: weekAdjustments,
      },
      nextWeek: {
        outlook: nextWeekOutlook,
        preparation: nextWeekPrep,
      },
    };
  }

  /**
   * Assess overall confidence in analysis
   */
  private assessConfidence() {
    const sessionsAnalyzed = this.sessions.length;
    const daysInSystem = this.profile?.dataQuality.timeInSystem || 0;
    
    const dataQuality = this.profile?.dataQuality.consistencyScore || 0;
    
    // Overall confidence increases with more data
    let overall = 0;
    if (sessionsAnalyzed >= 30 && dataQuality > 0.8) {
      overall = 0.9;
    } else if (sessionsAnalyzed >= 15 && dataQuality > 0.6) {
      overall = 0.7;
    } else if (sessionsAnalyzed >= 5) {
      overall = 0.5;
    } else {
      overall = 0.3;
    }

    return {
      overall,
      dataQuality,
      sessionsAnalyzed,
      daysInSystem,
      hasEnoughDataForML: sessionsAnalyzed >= 20,
    };
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private calculateFitness(): number {
    // Simplified fitness calculation (accumulated training effect)
    const last30Days = this.sessions.filter(s => {
      const sessionDate = new Date(s.date);
      const daysAgo = (Date.now() - sessionDate.getTime()) / (1000 * 60 * 60 * 24);
      const hasExercises = s.sessionType === 'range' ? true : (s as any).exercises && (s as any).exercises.length > 0;
      return daysAgo <= 30 && hasExercises;
    });

    const totalVolume = this.calculateVolume(last30Days);
    const avgVolumePerSession = totalVolume / Math.max(last30Days.length, 1);

    // Normalize to 0-100 scale (assuming 10k lbs/session is "high")
    return Math.min(100, (avgVolumePerSession / 10000) * 100);
  }

  private calculateFatigue(): number {
    /**
     * PERSONALIZED Time-Decay Fatigue Model
     * 
     * Key insight: Fatigue is RELATIVE to YOUR training capacity, not absolute.
     * If you regularly train 40k lbs/week, that's your baseline - not "high fatigue."
     * 
     * The model:
     * 1. Calculates YOUR chronic training baseline (30-day average weekly volume)
     * 2. Compares current acute load to YOUR personal norm
     * 3. Only flags elevated fatigue if current > personal baseline
     * 
     * This rewards consistent training: the more you train, the higher your capacity.
     * Backed by: Fitness-Fatigue Model (Banister), Individual Training Tolerance research
     */
    const DECAY_RATE = 0.23; // ~3 day half-life
    
    // Calculate YOUR personal chronic baseline (last 30 days average weekly volume)
    const chronicBaseline = this.calculatePersonalChronicLoad();
    
    const now = Date.now();
    let acuteDecayedLoad = 0;
    
    // Look at last 7 days for acute fatigue calculation
    const recentSessions = this.sessions.filter(s => {
      const sessionDate = new Date(s.date);
      const daysAgo = (now - sessionDate.getTime()) / (1000 * 60 * 60 * 24);
      const hasExercises = s.sessionType === 'range' ? true : (s as any).exercises && (s as any).exercises.length > 0;
      return daysAgo <= 7 && hasExercises;
    });
    
    recentSessions.forEach(session => {
      const sessionDate = new Date(session.date);
      const daysAgo = (now - sessionDate.getTime()) / (1000 * 60 * 60 * 24);

      // Fatigue load counts cardio + AMRAP + rucks using meter-based distances
      const sessionLoad = calculateSessionTrainingLoad(session);

      // Apply exponential decay based on days since session
      const decayFactor = Math.exp(-DECAY_RATE * daysAgo);
      acuteDecayedLoad += sessionLoad * decayFactor;
    });
    
    // PERSONALIZED FATIGUE: Compare acute load to YOUR chronic baseline
    // If you regularly train 40k/week, 35k acute is normal (not high fatigue)
    // Fatigue = (Acute / Chronic) - 1, scaled to 0-100
    // Ratio of 1.0 = 50% fatigue (your norm)
    // Ratio of 1.3 = 65% fatigue (30% above norm)
    // Ratio of 0.7 = 35% fatigue (30% below norm = recovering)
    
    let fatigue: number;
    if (chronicBaseline > 5000) {
      // Enough data for personalized calculation
      const acuteToChronicRatio = acuteDecayedLoad / chronicBaseline;
      // Map ratio to fatigue: 0.5x = 25%, 1.0x = 50%, 1.5x = 75%, 2.0x = 100%
      fatigue = Math.min(100, Math.max(0, Math.round(acuteToChronicRatio * 50)));
    } else {
      // Not enough history - use absolute scale with generous baseline
      const FALLBACK_NORMALIZER = 50000; // Generous for experienced lifters
      fatigue = Math.min(100, Math.round((acuteDecayedLoad / FALLBACK_NORMALIZER) * 100));
    }
    
    // Log with personalized context
    const sessionDetails = recentSessions.map(s => {
      const daysAgo = Math.round((Date.now() - new Date(s.date).getTime()) / (1000 * 60 * 60 * 24));
      return `${daysAgo}d`;
    }).join(', ');
    const chronicKLbs = (chronicBaseline / 1000).toFixed(0);
    const acuteKLbs = (acuteDecayedLoad / 1000).toFixed(0);
    console.log(` Fatigue: ${recentSessions.length} sessions [${sessionDetails}] | Acute: ${acuteKLbs}k vs Chronic: ${chronicKLbs}k → ${fatigue}% (personalized)`);
    
    return fatigue;
  }
  
  /**
   * Calculate personal chronic training load (30-day average weekly volume)
   * This represents YOUR training capacity - what's "normal" for you
   */
  private calculatePersonalChronicLoad(): number {
    const now = Date.now();
    const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
    
    // Get sessions from last 30 days
    const chronicSessions = this.sessions.filter(s => {
      const sessionDate = new Date(s.date).getTime();
      return sessionDate >= thirtyDaysAgo && sessionDate <= now;
    });
    
    const totalLoad = chronicSessions.reduce((sum, session) => sum + calculateSessionTrainingLoad(session), 0);

    // Return weekly average (divide by ~4 weeks)
    return totalLoad / 4;
  }

  /**
   * Strength tonnage in POUNDS. Nothing else.
   *
   * This used to also add `(load lbs × distance metres) / 10` for rucks,
   * carries and loaded cardio, plus a flat 2,000 for every range session — all
   * into one number surfaced as `strengthTonnageLbs` and rendered as
   * "Volume · lbs". A 5-mile ruck at 45 lb contributed roughly 36,200 to a
   * pounds total for a session containing no lifting, and a range trip added
   * 2,000 for turning up. The week-over-week arrow then reported "progress"
   * that was really just a change in which KIND of training the user did.
   *
   * Those quantities are not pounds and are already surfaced separately and
   * correctly: `calculateRuckLoadMiles` feeds `ruckLoadMiles` / `ruckMiles`,
   * and session count feeds `sessionsCompleted`. Folding them in here
   * double-counted ruck work AND corrupted the unit.
   */
  private calculateVolume(sessions: SessionLog[]): number {
    // Delegates to the single owner in volumeTracking so this figure and the
    // 5-Week Volume panel in metricsDataLoader cannot drift apart again. The
    // local copy this replaced also missed legacy pre-`sets` records, superset
    // rounds and drop segments, which the shared implementation handles.
    return calculateStrengthTonnageLbs(sessions);
  }

  private async predictNextSession(currentState: any, patterns: any) {
    // Try ML prediction first if available
    if (this.useML && this.mlPredictor) {
      try {
        const todayRecovery = this.recoveryData.find(r =>
          new Date(r.date).toDateString() === new Date().toDateString()
        );

        if (todayRecovery) {
          const recentSessions = this.sessions.slice(-10);
          const mlPrediction = await this.mlPredictor.predictPerformance(
            currentState.readiness,
            todayRecovery,
            recentSessions,
            this.todayActivity?.neatScore,
            this.todayActivity?.bodyFatPercent ?? undefined,
            this.nutritionData?.avgCalorieAdherence,
            this.nutritionData?.proteinPerLbBodyweight ?? undefined
          );

          // Map ML performance score to category
          let expected: 'poor' | 'below-average' | 'average' | 'good' | 'excellent';
          if (mlPrediction.expectedPerformance >= 80) expected = 'excellent';
          else if (mlPrediction.expectedPerformance >= 65) expected = 'good';
          else if (mlPrediction.expectedPerformance >= 50) expected = 'average';
          else if (mlPrediction.expectedPerformance >= 35) expected = 'below-average';
          else expected = 'poor';

          return {
            expected,
            confidence: mlPrediction.confidence,
            reasoning: `ML Prediction: ${mlPrediction.recommendation}`,
          };
        }
      } catch (error) {
        console.error(' ML prediction failed, falling back to heuristic:', error);
      }
    }

    // Fallback to heuristic prediction
    let expected: 'poor' | 'below-average' | 'average' | 'good' | 'excellent' = 'average';
    let confidence = 0.6;
    let reasoning = '';

    const performanceScore = currentState.readiness + (currentState.fitness - currentState.fatigue);

    if (performanceScore >= 120) {
      expected = 'excellent';
      confidence = 0.8;
      reasoning = 'High readiness, strong fitness, low fatigue - expect peak performance';
    } else if (performanceScore >= 90) {
      expected = 'good';
      confidence = 0.7;
      reasoning = 'Good conditions for solid training session';
    } else if (performanceScore >= 60) {
      expected = 'average';
      confidence = 0.6;
      reasoning = 'Standard training conditions';
    } else if (performanceScore >= 30) {
      expected = 'below-average';
      confidence = 0.7;
      reasoning = 'Fatigue or low readiness may limit performance';
    } else {
      expected = 'poor';
      confidence = 0.8;
      reasoning = 'High fatigue or very low readiness - performance likely compromised';
    }

    return { expected, confidence, reasoning };
  }

  private predictDeload(currentState: any, patterns: any) {
    let needed = false;
    let urgency: 'low' | 'medium' | 'high' | 'critical' = 'low';
    let recommendedDate: Date | null = null;
    let reasoning = '';

    // Check multiple factors
    const highFatigue = currentState.fatigue > 70;
    const lowReadiness = currentState.readiness < 50;
    const highRecoveryDebt = currentState.recoveryDebt > 60;
    const highProgramProgress = currentState.programProgress > 75;

    if (highFatigue && lowReadiness && highRecoveryDebt) {
      needed = true;
      urgency = 'critical';
      recommendedDate = new Date();
      reasoning = 'Multiple red flags: high fatigue, low readiness, high recovery debt. Deload immediately.';
    } else if ((highFatigue && lowReadiness) || (highFatigue && highRecoveryDebt)) {
      needed = true;
      urgency = 'high';
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      recommendedDate = nextWeek;
      reasoning = 'Fatigue accumulating faster than recovery. Plan deload within next week.';
    } else if (highProgramProgress) {
      needed = true;
      urgency = 'medium';
      const twoWeeks = new Date();
      twoWeeks.setDate(twoWeeks.getDate() + 14);
      recommendedDate = twoWeeks;
      reasoning = 'Nearing end of training cycle. Strategic deload recommended.';
    } else if (highFatigue || highRecoveryDebt) {
      needed = false;
      urgency = 'low';
      reasoning = 'Monitor fatigue levels. May need deload in 2-3 weeks.';
    } else {
      reasoning = 'Recovery is on track. Continue current training.';
    }

    return { needed, urgency, recommendedDate, reasoning };
  }

  private async predictInjuryRisk(currentState: any, patterns: any) {
    // Try ML prediction first if available
    if (this.useML && this.mlPredictor) {
      try {
        const todayRecovery = this.recoveryData.find(r => 
          new Date(r.date).toDateString() === new Date().toDateString()
        );
        
        if (todayRecovery) {
          const recentSessions = this.sessions.slice(-10);
          const mlPrediction = await this.mlPredictor.predictInjuryRisk(
            currentState.readiness,
            todayRecovery,
            recentSessions,
            this.todayActivity?.neatScore,
            this.todayActivity?.bodyFatPercent ?? undefined,
            this.nutritionData?.avgCalorieAdherence,
            this.nutritionData?.proteinPerLbBodyweight ?? undefined
          );

          // Map ML risk score to level
          let level: 'low' | 'moderate' | 'high' | 'critical';
          if (mlPrediction.riskScore >= 75) level = 'critical';
          else if (mlPrediction.riskScore >= 50) level = 'high';
          else if (mlPrediction.riskScore >= 30) level = 'moderate';
          else level = 'low';

          return {
            level,
            factors: mlPrediction.primaryFactors,
            confidence: mlPrediction.confidence,
          };
        }
      } catch (error) {
        console.error(' ML injury risk prediction failed, falling back to heuristic:', error);
      }
    }

    // Fallback to heuristic prediction with PERSONALIZED thresholds
    // Since fatigue is now personalized (50% = your norm), adjust thresholds accordingly
    const factors: string[] = [];
    let level: 'low' | 'moderate' | 'high' | 'critical' = 'low';
    let confidence = 0.6;

    // PERSONALIZED fatigue thresholds (50% = your baseline)
    // 70%+ means training 40% ABOVE your normal capacity
    // 80%+ means training 60% ABOVE your normal capacity - genuinely high
    if (currentState.fatigue > 80) {
      factors.push('Training significantly above your baseline');
      level = 'high';
    } else if (currentState.fatigue > 70) {
      factors.push('Elevated training load vs your norm');
      level = level === 'low' ? 'moderate' : level;
    }

    // Recovery debt is already based on ACWR which is personalized
    if (currentState.recoveryDebt > 70) {
      factors.push('High recovery debt');
      level = level === 'low' ? 'moderate' : 'high';
    }

    // Low readiness is still a concern
    if (currentState.readiness < 40) {
      factors.push('Very low readiness today');
      level = 'high';
    } else if (currentState.readiness < 55) {
      factors.push('Below average readiness');
      level = level === 'low' ? 'moderate' : level;
    }

    // Check if frequency exceeds learned capacity
    if (patterns.recoveryCapacity === 'low' && currentState.weeklySessionCount > patterns.optimalFrequency) {
      factors.push('Training frequency exceeds recovery capacity');
      level = level === 'low' ? 'moderate' : level;
    }

    // NEAT suppression as injury risk factor
    if (this.neatSuppression?.detected) {
      if (this.neatSuppression.severity === 'severe') {
        factors.push(`Severe NEAT suppression (${Math.round(this.neatSuppression.dropPercent)}% drop) - systemic fatigue`);
        if (level === 'low') level = 'moderate';
        else if (level === 'moderate') level = 'high';
      } else if (this.neatSuppression.severity === 'moderate') {
        factors.push('Moderate NEAT suppression detected');
        if (level === 'low') level = 'moderate';
      }
    }
    
    // POSITIVE FACTORS that reduce risk (reward consistent high-volume athletes)
    const chronicBaseline = this.calculatePersonalChronicLoad();
    if (chronicBaseline > 80000) { // 80k lbs/week = very high volume athlete
      // High-volume athlete - they're adapted to heavy training
      if (currentState.fatigue <= 60) {
        // Training at/below their norm - reduce risk level
        if (level === 'high') level = 'moderate';
        if (level === 'moderate') level = 'low';
      }
    }

    // Escalate to critical only if multiple genuine high-risk factors
    if (factors.length >= 3) {
      level = 'critical';
      confidence = 0.8;
    } else if (factors.length >= 2 && level === 'high') {
      confidence = 0.7;
    } else if (factors.length >= 1) {
      confidence = 0.6;
    } else {
      factors.push('Training within your normal capacity');
      confidence = 0.5;
    }

    return { level, factors, confidence };
  }
}

// ============================================================================
// INTELLIGENCE ENGINE CACHE
// Prevents re-computing the entire engine within the same page session.
// Invalidated on session save (via invalidateIntelligenceCache).
// ============================================================================
let _engineCache: { engine: UnifiedIntelligence; timestamp: number } | null = null;
const ENGINE_CACHE_TTL = 300_000; // 5 minutes

export function invalidateIntelligenceCache(): void {
  _engineCache = null;
}

/**
 * The readiness number a UI is allowed to SHOW, or null when there isn't one.
 *
 * `currentState.readiness` is always a number because ~40 downstream callers do
 * arithmetic and comparisons on it. But when `readinessIsProvisional` is set
 * that number is a CONSTANT (the 70 baseline) with nothing behind it — no
 * check-in today, no wearable signal, nothing ever logged.
 *
 * Rendering that constant produced the contradiction this exists to kill: a
 * green "Recovered — good to push (70)" sitting directly above today's order
 * saying "readiness is a placeholder, log your first session". Every persona
 * already has a null branch ("No recovery signal yet"), so returning null here
 * routes them into copy that is true.
 *
 * Display layers should use this. Math layers should keep using
 * `currentState.readiness` — a null would just become NaN downstream.
 */
export function displayReadiness(report: IntelligenceReport | null | undefined): number | null {
  const cs = report?.currentState;
  if (!cs) return null;
  if (cs.readinessIsProvisional) return null;
  return typeof cs.readiness === 'number' ? cs.readiness : null;
}

/**
 * Factory function to create intelligence engine (cached for 5 min)
 */
export async function createIntelligenceEngine(): Promise<UnifiedIntelligence> {
  // Return cached engine if still valid
  if (_engineCache && (Date.now() - _engineCache.timestamp) < ENGINE_CACHE_TTL) {
    console.log('[Intelligence] Returning cached engine');
    return _engineCache.engine;
  }
  // Load all necessary data from Dexie (single source of truth)
  let sessions: SessionLog[] = [];
  let userId: string | null = null;

  // Pre-fetch all data to avoid N+1 queries in calculateOperatorScore
  let oneRMs: Record<string, number> = {};
  let assessments: any[] = [];
  let bodyweight = 180;

  try {
    const { db, get1RMs } = await import('./db');
    const { getCurrentUserId } = await import('./anonymousAuth');
    userId = getCurrentUserId();

    if (userId) {
      // Batch all data fetches in parallel
      const { getSessionHistory } = await import('./sessionStorage');
      const [fetchedSessions, dexie1RMs] = await Promise.all([
        getSessionHistory(),
        get1RMs(userId),
      ]);

      oneRMs = dexie1RMs;
      sessions = fetchedSessions;

      if (sessions.length > 0) {
        const latestSession = sessions[sessions.length - 1]; // Last item = newest (oldest-first order)
        const latestDate = new Date(latestSession.date);
        console.log(` Loaded ${sessions.length} sessions (cached) for user ${userId}`);
        console.log(`   Latest session: ${latestDate.toLocaleDateString()} (${latestSession.sessionType})`);
      } else {
        console.log(` No sessions found for user ${userId}`);
      }

      // Load assessments from multiple sources
      // 1. Try Firebase (flat FitnessTest records from old assessment page)
      try {
        const { loadAssessmentsFromFirebase } = await import('./assessmentStorage');
        assessments = await loadAssessmentsFromFirebase(userId);
        console.log(`[Intelligence] Loaded ${assessments.length} assessments from Firebase`);
      } catch (assessErr) {
        console.warn(`[Intelligence] Firebase assessment load failed:`, assessErr);
        const assessmentsData = storage.get('fitnessTests');
        if (assessmentsData) {
          assessments = JSON.parse(assessmentsData);
          console.log(`[Intelligence] Loaded ${assessments.length} assessments from localStorage fallback`);
        }
      }

      // 2. Also check Dexie assessmentSessions (per-day assessment page saves here)
      try {
        const completedDexie = await db.assessmentSessions
          .where('odexieId')
          .equals(userId)
          .toArray();
        const completed = completedDexie.filter((a: any) => a.completedAt || a.day1);
        if (completed.length > 0) {
          console.log(`[Intelligence] Found ${completed.length} assessment sessions in Dexie`);
          const { convertAssessmentToSessionLog } = await import('./assessmentSystem');
          for (const session of completed) {
            // Convert structured session to a flat-ish format the grading pipeline can use
            const sessionLog = convertAssessmentToSessionLog(session);
            // Build a pseudo-FitnessTest from the structured data for the grading pipeline
            const pseudoTest: any = {
              id: session.id,
              userId: session.odexieId,
              date: new Date(session.startedAt),
              week: session.weekNumber,
              programId: session.programId,
              // Preserve structured day1/day3/day5 so convertFitnessTestToUnified can use them
              day1: session.day1,
              day3: session.day3,
              day5: session.day5,
              bodyweight: (session.day1 as any)?.weighIn?.weight,
            };
            // Only add if not already in the assessments array (by date proximity)
            const sessionDate = new Date(session.startedAt).getTime();
            const alreadyExists = assessments.some((a: any) =>
              Math.abs(new Date(a.date).getTime() - sessionDate) < 86400000
            );
            if (!alreadyExists) {
              assessments.push(pseudoTest);
              console.log(`[Intelligence] Added Dexie assessment from ${new Date(session.startedAt).toLocaleDateString()}`);
            }
          }
        }
      } catch (dexieErr) {
        console.warn(`[Intelligence] Dexie assessment load failed (non-critical):`, dexieErr);
      }

      if (assessments.length > 0) {
        console.log(`[Intelligence] Total assessments: ${assessments.length}`);
        console.log(`[Intelligence] Latest assessment keys:`, Object.keys(assessments[0]).filter(k => k !== 'userId'));
      }

      // Extract bodyweight from assessments
      if (assessments.length > 0) {
        const latest = assessments.sort((a: any, b: any) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        )[0];
        if (latest.bodyweight && latest.bodyweight > 0) {
          bodyweight = latest.bodyweight;
        }
      }

      // Fallback to user profile for bodyweight
      if (bodyweight === 180) {
        const userProfileData = storage.get('userProfile');
        if (userProfileData) {
          const userProfile = JSON.parse(userProfileData);
          if (userProfile.bodyWeight && userProfile.bodyWeight > 0) {
            bodyweight = userProfile.bodyWeight;
          }
        }
      }
    } else {
      console.warn(' No userId available for Dexie query');
    }
  } catch (error) {
    console.error(' Error loading sessions from Dexie:', error);
  }

  if (sessions.length === 0) {
    console.warn(' No sessions found in Dexie!');
  }

  const recoveryData = await getRecoveryHistory();

  console.log('� Unified Intelligence Engine Loading:');
  console.log(`   Sessions: ${sessions.length} | Recovery: ${recoveryData.length} data points`);

  // Try to get user profile, but don't fail if it doesn't exist
  let profile: UserAdaptiveProfile | null = null;
  try {
    if (userId) {
      profile = await getUserProfile(userId);
      console.log('   User profile loaded');
    } else {
      console.log('  ℹ No userId available. System will use statistical methods only.');
    }
  } catch (error) {
    console.log('  ℹ No user profile found. System will use statistical methods only.');
    // This is fine - system works without profile, just with reduced confidence
  }

  // Pre-fetch NEAT data (non-critical, optional)
  let todayActivity: IntelligenceInitData['todayActivity'] = null;
  let activityTrend: IntelligenceInitData['activityTrend'] = null;
  let neatSuppression: IntelligenceInitData['neatSuppression'] = null;
  let activityEntries: IntelligenceInitData['activityEntries'] = [];

  if (userId) {
    try {
      const { getTodayActivity, getDailyActivityEntries } = await import('./db');
      const activity = await getTodayActivity(userId);
      if (activity) {
        todayActivity = {
          steps: activity.steps,
          activeMinutes: activity.activeMinutes,
          activityZone: activity.activityZone,
          neatScore: activity.neatScore,
          // Typed reads. These were all `as any`, and the cast is what let
          // `sleepMinutes` — a field that exists on no type and in no
          // document — sit here reading permanently null. The real field is
          // `sleepDurationMinutes` (lib/db.ts:535); 111 production days of
          // sleep data were being discarded by the whole sleep-insight chain.
          hrv: activity.hrv ?? null,
          restingHeartRate: activity.restingHeartRate ?? null,
          sleepMinutes: activity.sleepDurationMinutes ?? null,
          activeCalories: activity.activeCalories ?? 0,
          weight: activity.weight ?? null,
          bodyFatPercent: activity.bodyFatPercent ?? null,
        };

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const entries = await getDailyActivityEntries(userId, { since: thirtyDaysAgo });

        // Store entries for biometric baseline calculations
        activityEntries = entries.map(e => ({
          hrv: (e as any).hrv,
          restingHeartRate: (e as any).restingHeartRate,
          weight: (e as any).weight,
          bodyFatPercent: (e as any).bodyFatPercent,
        }));

        if (entries.length >= 7) {
          const { analyzeActivityTrend, detectNEATSuppression } = await import('./neatCalculator');
          const trend = analyzeActivityTrend(entries);
          if (trend) {
            activityTrend = {
              avgSteps7d: trend.avgSteps7d,
              avgSteps30d: trend.avgSteps30d,
              trend: trend.trend,
              percentChange: trend.percentChange,
              suppressionDetected: trend.suppressionDetected,
            };
          }
          if (entries.length >= 14) {
            const suppression = detectNEATSuppression(entries);
            if (suppression?.detected) {
              neatSuppression = {
                detected: true,
                severity: suppression.severity,
                dropPercent: suppression.dropPercent,
                message: suppression.message,
              };
            }
          }
        }
      }
    } catch {
      // Non-critical: users without wearables see zero changes
    }
  }

  // Pre-fetch nutrition analysis (non-critical). Pass the user's body-comp
  // goal so calorie adherence is interpreted correctly: 'lose' counts
  // under-target as on-track, 'gain' counts over-target as on-track, and
  // 'maintain' keeps the ±10% band. Default falls back to 'maintain' when
  // the user didn't set a goal at onboarding.
  let nutritionData: NutritionAnalysisResult | null = null;
  if (userId) {
    try {
      const { getNutritionAnalysis } = await import('./nutritionService');
      let dietDirection: 'lose' | 'maintain' | 'gain' = 'maintain';
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db: firestoreDb } = await import('./firebase');
        const snap = await getDoc(doc(firestoreDb, 'users', userId));
        if (snap.exists()) {
          const goal = (snap.data() as { bodyCompGoal?: string }).bodyCompGoal;
          if (goal === 'lose' || goal === 'gain' || goal === 'maintain') {
            dietDirection = goal;
          }
        }
      } catch { /* non-critical — default to maintain */ }
      nutritionData = await getNutritionAnalysis(userId, 7, dietDirection);
    } catch {
      // Non-critical: users without nutrition data see no nutrition insights
    }
  }

  // Use smart scale / wearable weight if available (most recent and accurate source)
  if (activityEntries.length > 0) {
    const weightEntries = activityEntries
      .filter(e => e.weight != null && e.weight > 0)
      .map(e => e.weight!);
    if (weightEntries.length > 0) {
      // Use the most recent weight reading from smart scale
      const latestScaleWeight = weightEntries[weightEntries.length - 1];
      if (latestScaleWeight > 50 && latestScaleWeight < 500) { // sanity check
        bodyweight = latestScaleWeight;
        console.log(`[Intelligence] Using smart scale bodyweight: ${bodyweight} lbs`);
      }
    }
  }

  // Create the engine with pre-fetched data to avoid N+1 queries
  const initData: IntelligenceInitData = {
    userId,
    oneRMs,
    assessments,
    bodyweight,
    todayActivity,
    activityTrend,
    neatSuppression,
    activityEntries,
    nutritionData,
  };

  const engine = new UnifiedIntelligence(sessions, recoveryData, profile, initData);
  _engineCache = { engine, timestamp: Date.now() };
  return engine;
}

/**
 * Quick analysis (for dashboard)
 */
export async function getQuickIntelligence(): Promise<IntelligenceReport> {
  const engine = await createIntelligenceEngine();
  return engine.analyze();
}

